import { DatePipe } from '@angular/common';
import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatDrawer } from '@angular/material/sidenav';

import {
  catchError,
  concatMap,
  EMPTY,
  finalize,
  forkJoin,
  interval,
  Subject,
  Subscription,
  switchMap,
  takeUntil,
  timer,
} from 'rxjs';
import { ApiConstants } from 'src/app/constants/api-constants';
import { Messages } from 'src/app/constants/messages';
import {
  DataBroadcast,
  DataBroadcastModel,
  EntityAlertModel,
  EntityIconListPayloadModel,
  ResponseModelV2,
  EntityTrackPayloadModel,
  ZonePlanEntityModel,
  ZonePlanEntityPayloadModel,
  ZonePlanModel,
  EntityTrackModeModel,
  EntityTrackModel,
  ZonePlanEntityGroupModel,
  ZonePlanGroup,
  ButtonModel,
  ButtonType,
  ButtonColor,
  ActionMode,
  ButtonOption,
  ButtonIconColor,
  ZonePlanButtonModel,
  ZonePlanButtonGroup,
  ZonePlanGroupType,
  ZonePlanButtonGroupType,
  ZonePlanPlotConfigModel,
  ActionModeType,
  MousePointModel,
  ZonePlanEntityLocationPayloadModel,
  ResponseModel,
  DialogModel,
  DimensionModel,
  Position,
  EntityIconModel,
  EntityIconDataModel,
  ZonePlanEntityRelationalModel,
  ButtonSize,
  ZonePlanVisibleCategoryModel,
  ViewType,
  ViewSwitcherOptionModel,
  VisibilityType,
  Visibility,
  ZonePlanAggregationPayloadModel,
  ZonePlanAggregationCategoryModel,
} from 'src/app/model';
import { ZonesServiceService } from 'src/app/modules/plants/plant-details/zones/zones-service.service';
import { DataService, ServicecallService } from 'src/app/services';
import {
  DialogService,
  GeneralService,
  PlanogramSharedService,
} from 'src/app/shared/services';
import { FloorplanOtaUpdateComponent } from '../floorplan-ota-update/floorplan-ota-update.component';

@Component({
  selector: 'app-zone-plan',
  templateUrl: './zone-plan.component.html',
  styleUrls: ['./zone-plan.component.scss'],
})
export class ZonePlanComponent
  implements OnInit, OnDestroy, AfterViewChecked, AfterViewInit
{
  isRecentTimestamp: boolean;
  @Input()
  set zone(zonePlan: ZonePlanModel) {
    this._zone = zonePlan;
    this._planogramInitialize(true);
    this._fetchZonesData(true);
  }

  get zone() {
    return this._zone;
  }

  @Input()
  set entity(entityId: string) {
    // View entity location
    this._viewEntityLocation(entityId);
  }

  set planEntityList(value: ZonePlanEntityModel[]) {
    this._planEntityList = value;
    // Set plan entity list group
    this._setPlanEntityListGroup();
    // Set plot content
    this._setPlotContent();
    // Set all plot content to visible state
    this._setPlotContentState(true);
  }

  get planEntityList() {
    return this._planEntityList;
  }

  // Color mappings from shared service
  get summaryColorMapping() {
    return this._planogramSharedService.summaryColorMapping;
  }

  get backgroundColorMapping() {
    return this._planogramSharedService.backgroundColorMapping;
  }

  get historyBackgroundColorMapping() {
    return this._planogramSharedService.historyBackgroundColorMapping;
  }

  @ViewChild('planContainer') planContainer: ElementRef;

  @ViewChild('planImage', { static: false })
  planImageRef: ElementRef<HTMLImageElement>;

  @ViewChild('entityDrawer', { static: true }) entityDrawerRef: MatDrawer;

  @ViewChild('zoneGroupContainer', { static: true })
  zoneGroupContainerRef: ElementRef;

  @ViewChild('polygonCanvas') polygonCanvas!: ElementRef<HTMLCanvasElement>;

  @Output() headerDetials = new EventEmitter<any>();
  @Output() expand = new EventEmitter<any>();

  public planogramFilters: any = {};
  public planogramSummaryData: any[];
  public planogramEntityList: any[];
  public ActionMode = ActionMode;

  // Plot
  plotConfig: ZonePlanPlotConfigModel = {} as ZonePlanPlotConfigModel;
  plotContent: ZonePlanEntityModel[];
  plotAreaMode: ActionModeType = ActionMode.DEFAULT;
  plotAlertButton: ButtonModel;
  plotAssetButton: ButtonModel;
  selPlotContent: ZonePlanEntityModel = {} as ZonePlanEntityModel;
  selPlotBackButton: ButtonModel;
  markerPolygonId: string | null = null;

  // Zoom
  scale: number = 1;
  pointX: number = 0;
  pointY: number = 0;
  isDragging: boolean = false;
  startX: number = 0;
  startY: number = 0;
  lastX: number = 0;
  lastY: number = 0;
  minScale: number = 0.5;
  maxScale: number = 3;
  zoomStep: number = 0.1;
  showZoomControls: boolean = false;
  zoomControlsTimeout: any = null;

  // Plan entity
  planEntityListGroup: ZonePlanEntityGroupModel[];
  planEntityButtonList: ZonePlanButtonModel[];
  planEntityAlertButton: ButtonModel;
  planEntityAddButton: ZonePlanButtonModel;
  private _zone: ZonePlanModel;
  private _planEntityList: ZonePlanEntityModel[];
  private _viewEntityLocationFlag = new Subject<boolean>();
  private resizeTimeout: any;

  // Dimension
  entityDrawerDimension: DimensionModel;
  listAreaDimension: DimensionModel;

  // View Switcher
  viewSwitcherDisabled: boolean = false;
  viewSwitcherOptions: ViewSwitcherOptionModel[] = [];

  // Configuration
  private _entityIconList: EntityIconModel[];
  private _tenantGenericConfig: any;
  private _visibleCategoryList: ZonePlanVisibleCategoryModel;
  public categoryList: { [key: string]: string | undefined } = {};

  // Miscellaneous
  blobSasToken: string = ApiConstants.blobSasToken;
  readonly MESSAGES = Messages;
  readonly ZONE_PLAN_GROUP = ZonePlanGroup;
  showPopover = false;
  entityForCameraMapping: ZonePlanEntityModel;
  zoneData: any;
  showZoneNamePopover = false;
  newZoneName = '';
  isFocused = false;
  hoveredPolygonId: string | null = null;
  isDrawing = false;
  currentPolygon: { x: number; y: number }[] = [];
  polygons: any[] = [];
  selectedPolygonId: string | null = null;
  drawingMode = false;
  activeInfoPolygonId: string | null = null;
  ctx!: CanvasRenderingContext2D;
  imageNaturalWidth = 0;
  imageNaturalHeight = 0;
  displayWidth = 0;
  displayHeight = 0;
  overlayToggle: boolean = true;
  isFullScreen: boolean = false;

  //Tooltip
  activeTooltipEntityId: string | null = null;

  private _statusMapping: Record<string, string> = {};
  attributeList: any[] = [];
  private _statusMappingArray: Array<{
    name: string;
    value: string;
    bgTrackState?: string;
    summaryTrackState?: string;
    historyState?: string;
    statusColor?: string;
  }> = [];
  private _categoryList: { [key: string]: string | undefined } = {};

  // Subscription
  private _planEntitiesSubscription: Subscription;
  private _dataServiceEventSubscription: Subscription;
  private _createSubscription: Subscription;
  private _updateSubscription: Subscription;
  private _afterClosedSubscription: Subscription;
  private _deleteSubscription: Subscription;
  private _viewEntityLocationFlagSubscription: Subscription;
  private _planogramSummarySubscription: Subscription;
  private _planogramEntityListSubscription: Subscription;
  private _periodicApiSubscription: Subscription;
  private _entityDeviceAllocationSubscription: Subscription;
  private _entityDeviceDeallocationSubscription: Subscription;
  private _createNewZoneSubscription: Subscription;
  private _deleteZoneSubscription: Subscription;
  private _getZonesDataSubscription: Subscription;
  private _destroy$ = new Subject<void>();

  constructor(
    public _apiService: ServicecallService,
    private _dataService: DataService,
    private _datePipe: DatePipe,
    private _generalService: GeneralService,
    private _dialogService: DialogService,
    private _changeDetectorRef: ChangeDetectorRef,
    private _planogramSharedService: PlanogramSharedService,
    private _zoneService: ZonesServiceService,
    private _dialog: MatDialog,
  ) {
    // Set configuration
    this._setConfiguration();
    // Set visible category list
    this._setVisibleCategory();
    // Set button list
    this._setButtonList();
    // Set entity list group
    this._initPlanEntityListGroup();
  }

  ngOnInit(): void {
    this._periodicApiSubscription = interval(
      this._apiService.getConfiguration('tenantGenericConfig')?.[
        `2DPlanogramTimer`
      ] ?? 60000
    )
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
      if(!this.isFullScreen) {
        this._planogramInitialize();
      }
      });
    this._planogramSummarySubscription =
      this._planogramSharedService.planogramSummary$.subscribe((data) => {
        this.planogramSummaryData = data;
      if(this._apiService.getConfiguration('tenantGenericConfig')?.tenant !== 'EKA') {
        this.headerDetials.emit({
          content: this.planogramSummaryData,
          colorMapping: this.summaryColorMapping,
        });
      }
      });

    this._planogramEntityListSubscription =
      this._planogramSharedService.planogramEntityList$.subscribe((data) => {
        this.planogramEntityList = data;
        this._updatePlotContentFromEntityList();
      });
    // API Refresh
    this._dataServiceEventSubscription = this._dataService.events.subscribe({
      next: (broadcast: DataBroadcastModel) => {
        if (
          broadcast.name === DataBroadcast?.SITE_CHANGED ||
          broadcast.name === DataBroadcast.API_REFRESH
        ) {
          this._setConfiguration();
        }
        if (
          broadcast?.name === DataBroadcast.API_REFRESH ||
          broadcast?.name === DataBroadcast.TRACKER_ENTITY_LOCATION_CHANGED ||
          broadcast?.name === DataBroadcast.PAGE_CONTROL_DATE_CHANGE
        ) {
          this.planogramEntityList = null;
          this._planogramInitialize(true);
          this._fetchZonesData(true);
        }
      },
    });
  }

  ngAfterViewChecked(): void {
    // Set entity drawer style
    this._setDimension();
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
    this._planEntitiesSubscription?.unsubscribe();
    this._dataServiceEventSubscription?.unsubscribe();
    this._createSubscription?.unsubscribe();
    this._updateSubscription?.unsubscribe();
    this._afterClosedSubscription?.unsubscribe();
    this._deleteSubscription?.unsubscribe();
    this._viewEntityLocationFlagSubscription?.unsubscribe();
    this._planogramSummarySubscription?.unsubscribe();
    this._planogramEntityListSubscription?.unsubscribe();
    this._periodicApiSubscription?.unsubscribe();
    this._entityDeviceAllocationSubscription?.unsubscribe();
    this._entityDeviceDeallocationSubscription?.unsubscribe();
    this._createNewZoneSubscription?.unsubscribe();
    this._deleteZoneSubscription?.unsubscribe();
    this._getZonesDataSubscription?.unsubscribe();
    if (this.zoomControlsTimeout) {
      clearTimeout(this.zoomControlsTimeout);
    }

    this.headerDetials.emit({
      content: null,
      colorMapping: null,
    });

    document.removeEventListener('fullscreenchange', this.onFullscreenChange.bind(this));
  }

  @HostListener('window:resize', ['$event'])
  onWindowResize() {
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
    this.resizeTimeout = setTimeout(() => {
      this.setupCanvas();
      this._setPlotConfig();
      this.updateInfoIconPositions();
    }, 100);
  }

  /**
   * Initialize planogram API call
   */
  private _planogramInitialize(isLoaderNeeded: boolean = false) {
    if (this._zone) {
      this._planogramSharedService.initialize(
        this._zone,
        isLoaderNeeded,
        this.planogramFilters
      );
    }
  }

  /**
   * Determines whether plan entity header click on
   * @param event
   * @param planEntityList
   */
  onPlanEntityHeaderClick(
    event: MouseEvent,
    planEntityList: ZonePlanEntityGroupModel
  ) {
    // Set entity group based on user click
    this._setPlanEntityGroupType(planEntityList);
  }

  /**
   * Determines whether plan entity button click on
   * @param buttonContent
   * @param entity
   * @param planEntityList
   */
  onPlanEntityButtonClick(
    buttonContent: ZonePlanButtonModel,
    entity: ZonePlanEntityModel,
    planEntityList: ZonePlanEntityGroupModel
  ) {
    // Set plan entity button action
    this._setPlanEntityButtonAction(buttonContent, entity, planEntityList);
  }

  /**
   * Determines whether plan image load on
   */
  onPlanImageLoad() {
    // trigger event
  }

  /**
   * Determines whether plan image click on
   */
  onPlanImageClick(event: MouseEvent) {
    if (this.isDragging) {
      return;
    }
    event?.stopPropagation();
    this.activeTooltipEntityId = null;
    const clickPoint = {
      x: event.offsetX,
      y: event.offsetY,
    };
    const modeList = [ActionMode.ADD, ActionMode.EDIT];
    if (modeList.includes(this.plotAreaMode)) {
      // Check if the point is inside any polygon
      this.markerPolygonId = this.findPolygonContainingPoint(clickPoint);
      const activeEntity = this.planEntityList?.find(
        (item: any) => item?.active
      );
      if (
        this.categoryList['workBench'] === activeEntity?.category &&
        !this.markerPolygonId
      ) {
        this._apiService.toasterService({
          type: 'warning',
          msg: 'Please tag workbench inside a worktable',
        });
        return;
      }
      const offset: MousePointModel = {
        x: clickPoint.x * this.plotConfig?.saveAdjuster?.left,
        y: clickPoint.y * this.plotConfig?.saveAdjuster?.top,
      };
      this._setTempPlotContentOffset(offset);
      this._setButtonVisibility(true);
    }
    this._setEntityDrawerVisibility(Visibility.CLOSE);
  }

  /**
   * Gets icon data
   * @param type
   * @param category
   * @returns icon data
   */
  getIconData(type: number, category: string): EntityIconDataModel {
    return this._entityIconList
      ?.find(
        (iconGroup) => iconGroup?.entityType?.toString() === type?.toString()
      )
      ?.headerIconList?.find((iconData) => iconData?.category === category);
  }

  formatCamelCase = (str: string) => {
    return str ? str
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (firstChar) => firstChar.toUpperCase())
      .trim() : '';
  };

  /**
   * Gets tooltip position
   * @param offset
   * @returns tooltip position
   */
  getTooltipPosition(offset: MousePointModel): string {
    let tooltipClassList = [];
    const tooltip: DimensionModel = {
      width: 150 + 40,
      height: 75 + 40,
    };
    const displayOffset: MousePointModel = {
      x:
        offset?.x * this.plotConfig?.plotAdjuster?.left -
        this.plotConfig?.marker?.width / 2,
      y:
        offset?.y * this.plotConfig?.plotAdjuster?.top -
        this.plotConfig?.marker?.height / 2,
    };
    // Set tooltip position class: right / left
    tooltipClassList?.push(
      this.plotConfig?.viewPlan?.width > tooltip?.width + displayOffset?.x
        ? Position.RIGHT
        : Position.LEFT
    );
    // Set tooltip position class: below / above
    tooltipClassList?.push(
      this.plotConfig?.viewPlan?.height > tooltip?.height + displayOffset?.y
        ? Position.BELOW
        : Position.ABOVE
    );
    return tooltipClassList?.join('-');
  }

  /**
   * Determines whether view switcher select on
   * @param option
   */
  onViewSwitcherSelect(option: ViewSwitcherOptionModel) {
    switch (option?.dynamicType) {
      case ViewType.VIEW_ALL:
        this._setPlotContentState(true);
        break;
      default:
        this._setPlotContentStateByCategory(false, option?.dynamicType);
        break;
    }
  }

  /**
   * Determines whether plot icon click on
   * @param event
   * @param plot
   */
  onPlotIconClick(event: MouseEvent, plot: ZonePlanEntityModel) {
    if (this.isDragging) {
      return;
    }
    event?.stopPropagation();
    // Toggle tooltip
    this.activeTooltipEntityId = this.activeTooltipEntityId === plot?.entityId ? null : plot?.entityId;
    if (
      plot?.category &&
      this._visibleCategoryList?.[plot?.category]?.subCategoryTracked?.length
    ) {
      this._setEntityDrawerVisibility(Visibility.OPEN);
      this.selPlotContent = plot;
    }
  }

  /**
   * Determines whether back button click on
   * @param buttonContent
   */
  onBackButtonClick(buttonContent: ButtonModel) {
    this._setEntityDrawerVisibility(Visibility.CLOSE);
  }

  /**
   * Sets button list
   */
  private _setButtonList() {
    this.planEntityButtonList = [
      {
        type: ButtonType.BUTTON,
        option: ButtonOption.ICON,
        label: Messages?.BUTTON_LABEL_ZONE_PLAN_VIEW_MARKER,
        color: ButtonColor.TRANSPARENT,
        action: ActionMode?.VIEW,
        icon: 'general-eye-inactive-non-outline.svg',
        hoverIcon: 'general-eye-black.svg',
        group: ZonePlanButtonGroup.MAPPED,
        relationalGroup: ZonePlanButtonGroup.VIEW,
      },
      {
        type: ButtonType.BUTTON,
        option: ButtonOption.ICON,
        label: Messages?.BUTTON_LABEL_ZONE_PLAN_EDIT_MARKER,
        color: ButtonColor.TRANSPARENT,
        action: ActionMode?.EDIT,
        icon: 'general-marker-edit-inactive-non-outline.svg',
        hoverIcon: 'general-marker-edit-black.svg',
        group: ZonePlanButtonGroup.MAPPED,
        relationalGroup: ZonePlanButtonGroup.EDIT,
      },
      {
        type: ButtonType.BUTTON,
        option: ButtonOption.ICON,
        label: Messages?.BUTTON_LABEL_ZONE_PLAN_UNTAG_MARKER,
        color: ButtonColor.TRANSPARENT,
        action: ActionMode?.UNTAG,
        icon: 'general-untag-marker-inactive-non-outline.svg',
        hoverIcon: 'general-untag-marker-black.svg',
        group: ZonePlanButtonGroup.MAPPED,
        relationalGroup: null,
      },
      {
        type: ButtonType.BUTTON,
        option: ButtonOption.ICON,
        label: Messages?.BUTTON_LABEL_ZONE_PLAN_EXIT_VIEW,
        color: ButtonColor.TRANSPARENT,
        action: ActionMode?.CLEAR,
        icon: 'general-exit-view-inactive-non-outline.svg',
        hoverIcon: 'general-exit-view-black.svg',
        group: ZonePlanButtonGroup.VIEW,
        relationalGroup: ZonePlanButtonGroup.MAPPED,
      },
      {
        type: ButtonType.BUTTON,
        option: ButtonOption.ICON,
        label: Messages?.BUTTON_LABEL_ZONE_PLAN_APPLY,
        color: ButtonColor.PRIMARY,
        action: ActionMode?.UPDATE,
        icon: 'icon-zone-plan-apply.svg',
        group: ZonePlanButtonGroup.EDIT,
        iconColor: ButtonIconColor.ACTUAL,
        class: 'btn-pr-5-px',
        relationalGroup: ZonePlanButtonGroup.MAPPED,
        disabled: true,
      },
      {
        type: ButtonType.BUTTON,
        option: ButtonOption.ICON,
        label: Messages?.BUTTON_LABEL_CANCEL,
        color: ButtonColor.PRIMARY,
        action: ActionMode?.CANCEL,
        icon: 'icon-zone-plan-cancel.svg',
        group: ZonePlanButtonGroup.EDIT,
        iconColor: ButtonIconColor.ACTUAL,
        relationalGroup: ZonePlanButtonGroup.MAPPED,
      },
      {
        type: ButtonType.BUTTON,
        option: ButtonOption.ICON,
        label: Messages?.BUTTON_LABEL_ZONE_PLAN_APPLY,
        color: ButtonColor.PRIMARY,
        action: ActionMode?.SUBMIT,
        icon: 'icon-zone-plan-apply.svg',
        group: ZonePlanButtonGroup.ADD,
        iconColor: ButtonIconColor.ACTUAL,
        class: 'btn-pr-5-px',
        relationalGroup: ZonePlanButtonGroup.AVAILABLE,
        disabled: true,
      },
      {
        type: ButtonType.BUTTON,
        option: ButtonOption.ICON,
        label: Messages?.BUTTON_LABEL_CANCEL,
        color: ButtonColor.PRIMARY,
        action: ActionMode?.CANCEL,
        icon: 'icon-zone-plan-cancel.svg',
        group: ZonePlanButtonGroup.ADD,
        iconColor: ButtonIconColor.ACTUAL,
        relationalGroup: ZonePlanButtonGroup.AVAILABLE,
      },
      {
        type: ButtonType.BUTTON,
        option: ButtonOption.ICON,
        label: Messages?.BUTTON_LABEL_ZONE_PLAN_TAG_CAMERA,
        color: ButtonColor.TRANSPARENT,
        action: ActionMode?.TAGCAMERA,
        icon: 'zone-plan-cam-tag-green.svg',
        hoverIcon: 'zone-plan-cam-tag-black.svg',
        group: ZonePlanButtonGroup.TAGCAMERA,
        relationalGroup: null,
      },
      {
        type: ButtonType.BUTTON,
        option: ButtonOption.ICON,
        label: Messages?.BUTTON_LABEL_ZONE_PLAN_UNTAG_CAMERA,
        color: ButtonColor.TRANSPARENT,
        action: ActionMode?.UNTAGCAMERA,
        icon: 'zone-plan-cam-untag-green.svg',
        hoverIcon: 'zone-plan-cam-untag-black.svg',
        group: ZonePlanButtonGroup.UNTAGCAMERA,
        relationalGroup: null,
      },
    ];
    // Entity alert button
    this.planEntityAlertButton = {
      type: ButtonType.BUTTON,
      option: ButtonOption.ICON,
      label: Messages?.BUTTON_LABEL_ZONE_PLAN_ACTIVE_ALERT,
      color: ButtonColor.TRANSPARENT,
      action: ActionMode?.VIEW,
      icon: 'icon-zone-plan-active-alert.svg',
      iconColor: ButtonIconColor.ACTUAL,
    };
    // Entity add button
    this.planEntityAddButton = {
      type: ButtonType.BUTTON,
      option: ButtonOption.ICON,
      label: Messages?.BUTTON_LABEL_ZONE_PLAN_TAG_MARKER,
      color: ButtonColor.TRANSPARENT,
      action: ActionMode?.ADD,
      icon: 'general-tag-marker-green.svg',
      hoverIcon: 'general-tag-marker-black.svg',
      iconColor: ButtonIconColor.ACTUAL,
      group: ZonePlanButtonGroup.AVAILABLE,
      relationalGroup: ZonePlanButtonGroup.ADD,
    };
    // Plot alert button
    this.plotAlertButton = {
      type: ButtonType.BUTTON,
      option: ButtonOption.ICON,
      label: Messages?.BUTTON_LABEL_ALERT,
      color: ButtonColor.TRANSPARENT,
      action: ActionMode?.VIEW,
      icon: 'icon-zone-plan-active-alert.svg',
      iconColor: ButtonIconColor.ACTUAL,
      iconSize: 10,
      size: ButtonSize.SMALL,
    };
    // Plot asset button
    this.plotAssetButton = {
      type: ButtonType.BUTTON,
      option: ButtonOption.ICON,
      label: Messages?.BUTTON_LABEL_ALERT,
      color: ButtonColor.TRANSPARENT,
      action: ActionMode?.VIEW,
      icon: 'icon-zone-plan-asset.svg',
      iconColor: ButtonIconColor.ACTUAL,
      iconSize: 14,
      size: ButtonSize.SMALL,
    };
    // Back button
    this.selPlotBackButton = {
      type: ButtonType.BUTTON,
      option: ButtonOption.ICON,
      label: Messages?.BUTTON_LABEL_BACK,
      color: ButtonColor.PRIMARY,
      action: ActionMode.BACK,
      icon: 'icon-zone-plan-back.svg',
      iconColor: ButtonIconColor.ACTUAL,
    };
  }

  /**
   * Sets plan entity list group
   */
  private _initPlanEntityListGroup() {
    this.planEntityListGroup = [
      {
        title: Messages?.ZONE_PLAN_MAPPED_LABEL,
        active: true,
        activeEntity: false,
        list: [] as ZonePlanEntityModel[],
        listGroup: ZonePlanGroup?.MAPPED,
        buttonGroup: ZonePlanButtonGroup.MAPPED,
        disabled: false,
      },
      {
        title: Messages?.ZONE_PLAN_AVAILABLE_LABEL,
        active: false,
        activeEntity: false,
        list: [] as ZonePlanEntityModel[],
        listGroup: ZonePlanGroup?.AVAILABLE,
        buttonGroup: ZonePlanButtonGroup.AVAILABLE,
        disabled: false,
      },
    ];
  }

  /**
   * Gets plan entities
   * @param zonePlan
   */
  private _getPlanEntities() {
    let planBaseEntityList: ZonePlanEntityModel[];
    this._planEntitiesSubscription?.unsubscribe();
    this._planEntitiesSubscription = timer(0, this._apiService.getConfiguration('tenantGenericConfig')?.['floorplanRefreshRate'] ?? 10000)
      .pipe(
        switchMap(() => {
          const combinedZoneIds = this.zone?._id
            ? [this.zone._id, ...this.zoneData.map((zone: any) => zone._id)]
            : [...this.zoneData.map((zone: any) => zone._id)];
          const payload: Partial<ZonePlanEntityPayloadModel> = {
            siteId: sessionStorage.getItem('siteId')
              ? JSON.parse(sessionStorage.getItem('siteId') || '{}')
              : null,
            zoneIds: combinedZoneIds,
            // hierarchyId: this.zone?._id ?? null,
    };
      return this._apiService.getFloorPlanEntities(payload, false);
        }),
        concatMap((result: ZonePlanEntityModel[]) => {
          let subCategoryTracked: string[] = [];
          // Getting Subcategory tracked list
          this._generalService
            .convertObjectToArray(this._visibleCategoryList)
            ?.map((visibleCategory) => {
              if (visibleCategory?.value?.['subCategoryTracked']?.length) {
                subCategoryTracked.push(
                  ...visibleCategory?.value?.['subCategoryTracked']
                );
              }
            });
          subCategoryTracked = [...new Set(subCategoryTracked)];
          planBaseEntityList = result?.filter(
            (planBaseEntity) =>
              (Object.keys(this._visibleCategoryList)?.length ||
                Object.keys(this._visibleCategoryList)?.includes(
                  planBaseEntity?.category
                )) &&
              !subCategoryTracked?.includes(planBaseEntity?.category)
          );
          const entityIdList = [...planBaseEntityList]?.map(
            (planEntity) => planEntity?.entityId
          );
          const [startTime, endTime] =
            this._generalService?.getStartEndTimeByDate();
          // Track entity payload
          const payloadTrackEntity: Partial<EntityTrackPayloadModel> = {
            siteId: sessionStorage.getItem('siteId')
              ? JSON.parse(sessionStorage.getItem('siteId') || '{}')
              : null,
            from: this._datePipe.transform(startTime, 'yyyy-MM-ddTHH:mm:ssZZ'),
            to: this._datePipe.transform(Date.now(), 'yyyy-MM-ddTHH:mm:ssZZ'),
            entityIds: entityIdList,
            limit: 10000, //For no pagination
          };
          // Icon list payload
          const payloadEntityIcon: Partial<EntityIconListPayloadModel> = {
            from: this._datePipe.transform(startTime, 'yyyy-MM-ddTHH:mm:ssZZ'),
            to: this._datePipe.transform(Date.now(), 'yyyy-MM-ddTHH:mm:ssZZ'),
            entityId: entityIdList,
          };
          // Entity aggregation payload
          const payloadEntityAggregation: ZonePlanAggregationPayloadModel = {
            siteId: sessionStorage.getItem('siteId')
              ? JSON.parse(sessionStorage.getItem('siteId') || '{}')
              : null,
            zoneId: this.zone?._id,
            categories: this._getAggregationCategoryList(),
          };
          return forkJoin([
            this._apiService.trackEntity(payloadTrackEntity, 1),
            this._apiService.iconList(payloadEntityIcon),
            this._apiService.entityAggregation(payloadEntityAggregation),
          ]);
        })
      )
      .subscribe({
        next: (
          result: [
            ResponseModelV2<EntityTrackModeModel[]>,
            ResponseModelV2<EntityAlertModel[]>,
            ZonePlanEntityRelationalModel[]
          ]
        ) => {
          let [
            trackEntityResult,
            trackEntityAlertResult,
            entityAggregationResult,
          ] = result;
          const nonPlanEntityList =
            entityAggregationResult ?? ([] as ZonePlanEntityRelationalModel[]);
          // Get plan entity list
          this.planEntityList = this._getPlanEntityList(
            planBaseEntityList,
            trackEntityResult?.response?.data?.[0]?.original,
            trackEntityAlertResult?.response?.data,
            nonPlanEntityList
          );
          if(this._apiService.getConfiguration('tenantGenericConfig')?.tenant === 'EKA') {
            const taggedDevices = this.planEntityList?.filter(plant => (plant?.zones?.length && !plant?.available && plant?.category !== 'Gateway')) || []; //Consider only the tagged devices
            const statusSummary = this.getStateSummary(taggedDevices, this._statusMappingArray); 
            this.headerDetials.emit({
              content: statusSummary,
              colorMapping: this.summaryColorMapping,
            });
          }
          // Set view switcher category
          this._setViewSwitcherCategory();
          // Set view entity location flag
          this._viewEntityLocationFlag?.next(true);
        },
      });
  }

  /**
   * Gets plan entity list
   * @param zonePlan
   * @param planEntityList
   * @param trackEntityList
   * @param trackEntityAlertList
   * @returns plan entity list
   */
  private _getPlanEntityList(
    planEntityList: ZonePlanEntityModel[],
    trackEntityList: EntityTrackModel[],
    trackEntityAlertList: EntityAlertModel[],
    nonPlanEntityList: ZonePlanEntityRelationalModel[]
  ): ZonePlanEntityModel[] {
    return planEntityList?.map((planEntityContent: ZonePlanEntityModel) => {
      // Find nonPlanEntity using entity id
      const nonPlanEntity = nonPlanEntityList?.find(
        (nonPlanEntity) =>
          nonPlanEntity?.trackerId === planEntityContent?.deviceName
      );
      // Find corresponding track entity to get attributes
      const trackEntity = trackEntityList?.find(
        (trackEntityContent) =>
          trackEntityContent?.entityId === planEntityContent?.entityId
      );
      // Find _highlightedCategoryConfigList using category
      const highlightedCategoryConfig =
        this._visibleCategoryList?.[planEntityContent?.category];

      const deviceThreshold = this._apiService.getConfiguration('tenantGenericConfig')?.deviceThresholdInSec ?? 60;
      
      // Check if trackEntity.sourceTimestamp falls within last 60 seconds
      const isRecentTimestamp = this._isTimestampWithinLastSeconds(trackEntity?.sourceTimestamp, deviceThreshold);
      
      // Merge sourceTimestamp & alertsCount in plan entity list
      return {
        ...planEntityContent,
        active: false,
        attributes: (trackEntity?.attributes ?? []).map((attr: any) => ({
         ...attr,
         isRecentTimestamp,  
       })),
        status: isRecentTimestamp ? trackEntity?.attributes?.find(
          (attribute) => attribute?.attributeName === 'Status'
        )?.attributeValue : '2.0',
        isRecentTimestamp,
        lastActiveTime:
          this._apiService.getLastSeenFormat(
            trackEntityList?.find(
              (trackEntityContent) =>
                trackEntityContent?.entityId === planEntityContent?.entityId
            )?.sourceTimestamp
          ) ?? Messages.TEXT_NA,
        alertsCount:
          trackEntityAlertList?.find(
            (trackEntityAlertContent) =>
              trackEntityAlertContent?._id === planEntityContent?.entityId
          )?.alertsCount ?? 0,
        healthStatus: highlightedCategoryConfig?.showHealthStatus ?? true,
        relationalEntity: {
          count: highlightedCategoryConfig?.subCategoryTracked?.length
            ? nonPlanEntity?.detectedCategoryDetails?.reduce(
                (accumulator: number, relationalEntity) => {
                  return accumulator + relationalEntity?.count;
                },
                0
              ) ?? 0
            : 0,
          alertsCount: highlightedCategoryConfig?.subCategoryTracked?.length
            ? nonPlanEntity?.detectedCategoryDetails?.reduce(
                (accumulator: number, relationalEntity) => {
                  return accumulator + relationalEntity?.alertsCount;
                },
                0
              ) ?? 0
            : 0,
          entityIds: highlightedCategoryConfig?.subCategoryTracked?.length
            ? nonPlanEntity?.detectedCategoryDetails?.reduce(
                (accumulator: string[], relationalEntity) => {
                  return [...accumulator, ...relationalEntity?.entityIds];
                },
                []
              ) ?? []
            : [],
        },
      } as ZonePlanEntityModel;
    });
  }

  getStateSummary(input: any[], statusTemplate: any[]) {
      const result = [
        { trackedState: null, count: 0, name: 'Total' },
        ...statusTemplate.map(t => ({
          trackedState: t.value,
          name: t.name,
          count: 0
        }))
      ];

      const getState = (item: any): string | null => {
        if (item.status) return item.status;

        const attr = item.attributes?.find(
          (a: any) => a.attributeName?.toLowerCase() === 'status'
        );

        return attr?.attributeValue || null;
      };

      const map = new Map(result.map(r => [r.trackedState, r]));

      input.forEach(item => {
        const rawState = getState(item);

        const state = rawState ?? '2.0';

        map.get(null)!.count++;

        if (map.has(state)) {
          map.get(state)!.count++;
        }
      });

     return result;
  }

  /**
   * Sets plan entity list group
   */
  private _setPlanEntityListGroup() {
    this.planEntityListGroup = this.planEntityListGroup?.map(
      (entityList: ZonePlanEntityGroupModel) => {
        return {
          ...entityList,
          list:
            entityList?.listGroup === ZonePlanGroup.MAPPED
              ? this._getMappedEntityList()
              : this._getAvailableEntityList(),
        };
      }
    );
  }

  /**
   * Sets plot content
   */
  private _setPlotContent() {
    this.plotContent = this.planEntityListGroup?.find(
      (entityList: ZonePlanEntityGroupModel) =>
        entityList?.listGroup === ZonePlanGroup.MAPPED
    )?.list;
    // After data is fetched, update plotContent with additional information
    this._updatePlotContentFromEntityList();
  }

  /**
   * Gets mapped entity list
   * @returns mapped entity list
   */
  private _getMappedEntityList(): ZonePlanEntityModel[] {
    const zoneIdsToCheck = [
      this.zone?._id,
      ...this.zoneData.map((zone: any) => zone._id),
    ];

    return this.planEntityList?.filter(
      (entityContent) =>
        !entityContent?.available &&
        entityContent?.zones?.some((zoneId) => zoneIdsToCheck.includes(zoneId))
    );
  }

  /**
   * Gets available entity list
   * @returns available entity list
   */
  private _getAvailableEntityList(): ZonePlanEntityModel[] {
    return this.planEntityList?.filter(
      (entityContent) =>
        entityContent?.available && !entityContent?.zones?.length
    );
  }

  /**
   * Sets plan entity group type
   * @param planEntityList
   */
  private _setPlanEntityGroupType(planEntityList: ZonePlanEntityGroupModel) {
    this.planEntityListGroup = this.planEntityListGroup?.map(
      (entityList: ZonePlanEntityGroupModel) => {
        return {
          ...entityList,
          active: entityList?.listGroup === planEntityList?.listGroup,
        };
      }
    );
  }

  /**
   * Sets button relational group
   * @param relationalGroup
   * @param listGroup
   */
  private _setButtonRelationalGroup(
    relationalGroup: ZonePlanButtonGroupType,
    listGroup: ZonePlanGroupType
  ) {
    if (relationalGroup) {
      this.planEntityListGroup = this.planEntityListGroup?.map(
        (entityList: ZonePlanEntityGroupModel) => {
          return {
            ...entityList,
            buttonGroup:
              entityList?.listGroup === listGroup
                ? relationalGroup
                : entityList?.buttonGroup,
            activeEntity:
              entityList?.listGroup === listGroup
                ? !entityList?.activeEntity
                : entityList?.activeEntity,
            disabled:
              entityList?.listGroup !== listGroup
                ? !entityList?.disabled
                : entityList?.disabled,
          };
        }
      );
    }
  }

  /**
   * Sets active plan entity
   * @param selEntity
   */
  private _setActivePlanEntity(selEntity: ZonePlanEntityModel) {
    this.planEntityList = [...this.planEntityList]?.map(
      (entityContent: ZonePlanEntityModel) => {
        return {
          ...entityContent,
          active:
            entityContent?.entityId === selEntity?.entityId
              ? !entityContent?.active
              : entityContent?.active,
        };
      }
    );
  }

  /**
   * Sets basic plot config
   */
  private _setBasicPlotConfig() {
    if (!this.planImageRef?.nativeElement) {
      return;
    }
    const naturalWidth =
      this.imageNaturalWidth ||
      this.planImageRef.nativeElement.naturalWidth ||
      0;
    const naturalHeight =
      this.imageNaturalHeight ||
      this.planImageRef.nativeElement.naturalHeight ||
      0;
    this.plotConfig = {
      ...this.plotConfig,
      plan: {
        width: naturalWidth,
        height: naturalHeight,
      },
      marker: {
        width: 40,
        height: 40,
      },
    };
  }

  /**
   * Sets plot config
   */
  private _setPlotConfig() {
    if (this.plotConfig?.plan?.width && this.plotConfig?.plan?.height) {
      this.plotConfig = {
        ...this.plotConfig,
        viewPlan: {
          width: this.planImageRef?.nativeElement?.clientWidth,
          height: this.planImageRef?.nativeElement?.clientHeight,
        },
        plotAdjuster: {
          left:
            this.planImageRef?.nativeElement?.clientWidth /
            this.plotConfig?.plan?.width,
          top:
            this.planImageRef?.nativeElement?.clientHeight /
            this.plotConfig?.plan?.height,
        },
        saveAdjuster: {
          left:
            this.plotConfig?.plan?.width /
            this.planImageRef?.nativeElement?.clientWidth,
          top:
            this.plotConfig?.plan?.height /
            this.planImageRef?.nativeElement?.clientHeight,
        },
      };
    }
  }

  /**
   * Sets plot content state
   * @param state
   * @param [entity]
   */
  private _setPlotContentState(
    state: boolean,
    entity: ZonePlanEntityModel = {} as ZonePlanEntityModel
  ) {
    // Set plot content state
    this.plotContent = this.plotContent?.map(
      (planEntity: ZonePlanEntityModel) => {
        return {
          ...planEntity,
          active:
            planEntity?.entityId === entity?.entityId
              ? planEntity?.active
              : state,
        };
      }
    );
  }

  /**
   * Sets plot area mode
   * @param mode
   */
  private _setPlotAreaMode(mode: ActionModeType) {
    this.plotAreaMode = mode;
  }

  /**
   * Sets plot content as temp
   * @param entity
   */
  private _setPlotContentAsTemp(entity: ZonePlanEntityModel) {
    switch (this.plotAreaMode) {
      case ActionMode.ADD:
        this.plotContent?.push(entity);
        break;
      case ActionMode.EDIT:
        this.plotContent?.push({
          ...entity,
          available: true,
          active: true,
        });
        break;
    }
  }

  /**
   * Sets temporary plot content offset
   * @param offset
   */
  private _setTempPlotContentOffset(offset: MousePointModel) {
    const plotContentTemp = [...this.plotContent]
      ?.filter((planEntity: ZonePlanEntityModel) => planEntity?.available)
      ?.map((plot) => {
        return {
          ...plot,
          active: true,
          coordinates: {
            x: offset?.x,
            y: offset?.y,
          },
        };
      });
    this.plotContent = [...this.plotContent]
      ?.filter((planEntity: ZonePlanEntityModel) => !planEntity?.available)
      ?.concat(plotContentTemp);
  }

  /**
   * Removes temp plot content
   */
  private _removeTempPlotContent() {
    this.plotContent = [...this.plotContent]?.filter(
      (planEntity: ZonePlanEntityModel) => !planEntity?.available
    );
  }

  /**
   * Saves plot content
   * @param plotContent
   */
  private _savePlotContent(plotContent: ZonePlanEntityModel[]) {
    const payloadContent = this._setPayloadContent(plotContent);
    this._createSubscription = this._apiService
      .createEntityLocation(payloadContent)
      .subscribe({
        next: (result: ResponseModel<null>) => {
          this._dataService.broadcast(
            DataBroadcast.TRACKER_ENTITY_LOCATION_CHANGED
          );
          this._apiService.toasterService({
            type: 'success',
            msg: result?.message,
          });
          this.markerPolygonId = null;
        },
        error: (result) => {
          this._apiService.toasterService({
            type: 'error',
            msg: result?.message,
          });
          this.markerPolygonId = null;
        },
      });
  }

  /**
   * Updates plot content
   * @param plotContent
   */
  private _updatePlotContent(plotContent: ZonePlanEntityModel[]) {
    const payloadContent = this._setPayloadContent(plotContent);
    this._updateSubscription = this._apiService
      .updateEntityLocation(payloadContent)
      .subscribe({
        next: (result: ResponseModel<null>) => {
          this._dataService.broadcast(
            DataBroadcast.TRACKER_ENTITY_LOCATION_CHANGED
          );
          this._apiService.toasterService({
            type: 'success',
            msg: result?.message,
          });
          this.markerPolygonId = null;
        },
        error: (result) => {
          this._apiService.toasterService({
            type: 'error',
            msg: result?.message,
          });
          this.markerPolygonId = null;
        },
      });
  }

  /**
   * Deletes plot content
   * @param entity
   */
  private _deletePlotContent(entity: ZonePlanEntityModel) {
    // Dialog config
    const dialogConfig: MatDialogConfig<DialogModel> = {
      data: {
        title: this._labelBasedOnCategory(entity?.category, 'title'),
        content: this._labelBasedOnCategory(entity?.category, 'content'),
        actions: [
          {
            label: Messages?.BUTTON_LABEL_CANCEL,
            type: ButtonType?.BUTTON,
          },
          {
            label: Messages?.BUTTON_LABEL_SUBMIT,
            type: ButtonType?.BUTTON,
            value: true,
            color: ButtonColor.PRIMARY,
          },
        ],
      },
    };
    this._afterClosedSubscription = this._dialogService
      ?.showDialog(dialogConfig)
      ?.afterClosed()
      ?.subscribe({
        next: (response: boolean) => {
          if (response) {
            this._deleteSubscription = this._apiService
              .deleteEntityLocation({
                entityId: Array.of(entity?.entityId),
                siteId: sessionStorage.getItem('siteId')
                  ? JSON.parse(sessionStorage.getItem('siteId') || '{}')
                  : null,
              })
              .subscribe({
                next: (result: ResponseModel<null>) => {
                  this._dataService.broadcast(
                    DataBroadcast.TRACKER_ENTITY_LOCATION_CHANGED
                  );
                  this._apiService.toasterService({
                    type: 'success',
                    msg: result?.message,
                  });
                },
              });
          }
        },
      });
  }

  /**
   * Deletes plot content
   * @param entity
   */
  private _unTagCamera(entity: ZonePlanEntityModel) {
    // Dialog config
    const dialogConfig: MatDialogConfig<DialogModel> = {
      data: {
        title: Messages?.ZONE_PLAN_DIALOG_UNTAG_CAMERA_TITLE,
        content:
          Messages?.ZONE_PLAN_DIALOG_UNTAG_CAMERA_CONTENT +
          ' ' +
          entity?.deviceName +
          '?',
        actions: [
          {
            label: Messages?.BUTTON_LABEL_CANCEL,
            type: ButtonType?.BUTTON,
          },
          {
            label: Messages?.BUTTON_LABEL_SUBMIT,
            type: ButtonType?.BUTTON,
            value: true,
            color: ButtonColor.PRIMARY,
          },
        ],
      },
    };
    this._afterClosedSubscription = this._dialogService
      ?.showDialog(dialogConfig)
      ?.afterClosed()
      ?.subscribe({
        next: (confirmed: boolean) => {
          if (!confirmed) {
            return;
          }
          let siteId = null;
          try {
            const siteIdStr = sessionStorage.getItem('siteId');
            siteId = siteIdStr ? JSON.parse(siteIdStr) : null;
          } catch (e) {
            console.error('Error parsing siteId from session storage:', e);
          }
          const payload = {
            siteId,
            deviceId: entity?.deviceOid,
            deviceType: null,
            currentTime: this._datePipe.transform(
              new Date(),
              'yyyy-MM-ddTHH:mm:ssZ'
            ),
            entityId: entity?.entityId,
          };
          this._entityDeviceDeallocationSubscription?.unsubscribe();
          this._entityDeviceDeallocationSubscription = this._apiService
            .entityDeviceDeallocation(payload)
            .subscribe({
              next: (response: any) => {
                const isSuccess = !!response?.status;
                this._apiService.toasterService({
                  type: isSuccess ? 'success' : 'error',
                  msg:
                    response?.response?.message ||
                    (isSuccess
                      ? 'Device deallocated successfully'
                      : 'Failed to deallocate device'),
                });
                if (isSuccess) {
                  this._dataService.broadcast(
                    DataBroadcast.TRACKER_ENTITY_LOCATION_CHANGED
                  );
                }
              },
              error: (error) => {
                console.error('Device deallocation error:', error);
                this._apiService.toasterService({
                  type: 'error',
                  msg: 'Error occurred while deallocating device',
                });
              },
            });
        },
        error: (error) => {
          console.error('Dialog interaction error:', error);
        },
      });
  }

  /**
   * Sets payload content
   * @param plotContent
   * @returns payload content
   */
  private _setPayloadContent(
    plotContent: ZonePlanEntityModel[]
  ): ZonePlanEntityLocationPayloadModel {
    const entity: ZonePlanEntityModel = plotContent?.find(
      (planEntity: ZonePlanEntityModel) => planEntity?.available
    );
    return {
      entityId: entity?.entityId,
      mappingId: entity?.mappingId,
      coordinates: entity?.coordinates,
      zones: Array.of(this.markerPolygonId ?? this.zone?._id),
    };
  }

  /**
   * Sets button visibility
   * @param action
   * @param state
   */
  private _setButtonVisibility(state: boolean) {
    // Get action based on current value of plotAreaMode
    const action: ActionModeType =
      this.plotAreaMode === ActionMode.ADD
        ? ActionMode.SUBMIT
        : ActionMode.UPDATE;
    this.planEntityButtonList.find(
      (button) => button?.action === action
    ).disabled = !state;
  }

  /**
   * Sets configuration
   */
  private _setConfiguration() {
    this._entityIconList = this._apiService?.getConfiguration('entityIconList')
      ? this._apiService?.getConfiguration('entityIconList')?.entityIconList
      : [];
    this._tenantGenericConfig = this._apiService.getConfiguration(
      'tenantGenericConfig'
    )
      ? this._apiService.getConfiguration('tenantGenericConfig')
      : null;
    this.categoryList = this._tenantGenericConfig?.entityCategories ?? {};

    const filterFields =
      this._apiService.getConfiguration('entityFilter')?.filterPlanogramFields ??
      {};
    this._categoryList =
      this._apiService.getConfiguration('tenantGenericConfig')?.entityCategories ??
      {};
    this.attributeList = this._apiService.getConfiguration('tenantGenericConfig')?.attributeList ?? [];
    // Validate and map status field values
    this._statusMapping =
      filterFields?.['status']?.fieldValues || {};

    // Populate status mapping array with full objects for color lookup
    const statusFieldValues = filterFields?.['status']?.fieldValues;
    if (statusFieldValues && typeof statusFieldValues === 'object') {
      this._statusMappingArray = Object.values(statusFieldValues).filter(
        item => item && typeof item === 'object'
      ) as Array<{
        name: string;
        value: string;
        bgTrackState?: string;
        summaryTrackState?: string;
        historyState?: string;
        statusColor?: string;
      }>;
    }

  }

  /**
   * Sets plan entity button action
   * @param buttonContent
   * @param entity
   * @param planEntityList
   */
  private _setPlanEntityButtonAction(
    buttonContent: ZonePlanButtonModel,
    entity: ZonePlanEntityModel,
    planEntityList: ZonePlanEntityGroupModel
  ) {
    // Save current plot content as temporary data
    const plotContentTemp = [...this.plotContent];
    // Set active entity & relationalGroup only if the button has relationalGroup values
    if (buttonContent?.relationalGroup) {
      // Set active entity in plan entity list
      this._setActivePlanEntity(entity);
      // Set buttonGroup property of planEntityListGroup to show relational buttons
      this._setButtonRelationalGroup(
        buttonContent?.relationalGroup,
        planEntityList?.listGroup
      );
    }
    // Button action
    switch (buttonContent?.action) {
      case ActionMode.ADD:
        // Set all plot content to invisible state
        this._setPlotContentState(false);
        // Set plot area mode to 'add'
        this._setPlotAreaMode(ActionMode.ADD);
        // Set plot content as temporary
        this._setPlotContentAsTemp(entity);
        // Set all switcher option to disable state
        this._setViewSwitcherOptionState(false);
        break;

      case ActionMode.CANCEL:
        // Set button visibility
        this._setButtonVisibility(false);
        // Remove temporary added plot content
        this._removeTempPlotContent();
        // Set plot area mode to 'cancel'
        this._setPlotAreaMode(ActionMode.CANCEL);
        // Set all switcher option to disable state except first option
        this._setViewSwitcherOptionState(false, ViewType.VIEW_ALL);
        break;

      case ActionMode?.EDIT:
        // Set all plot content to invisible state
        this._setPlotContentState(false);
        // Set plot area mode to 'edit'
        this._setPlotAreaMode(ActionMode.EDIT);
        // Set plot content as temporary
        this._setPlotContentAsTemp(entity);
        // Set all switcher option to disable state
        this._setViewSwitcherOptionState(false);
        break;

      case ActionMode?.SUBMIT:
        // Save new entity location
        this._savePlotContent(plotContentTemp);
        // Set plot area mode to 'default'
        this._setPlotAreaMode(ActionMode.DEFAULT);
        // Set all switcher option to disable state except first option
        this._setViewSwitcherOptionState(false, ViewType.VIEW_ALL);
        break;

      case ActionMode?.UPDATE:
        // Update entity location
        this._updatePlotContent(plotContentTemp);
        // Set plot area mode to 'default'
        this._setPlotAreaMode(ActionMode.DEFAULT);
        // Set all switcher option to disable state except first option
        this._setViewSwitcherOptionState(false, ViewType.VIEW_ALL);
        break;

      case ActionMode?.UNTAG:
        this._deletePlotContent(entity);
        break;

      case ActionMode?.VIEW:
        // Set all plot content to invisible state except entity
        this._setPlotContentState(false, entity);
        // Set all switcher option to disable state
        this._setViewSwitcherOptionState(false);
        break;

      case ActionMode?.CLEAR:
        // Set all switcher option to disable state except first option
        this._setViewSwitcherOptionState(false, ViewType.VIEW_ALL);
        break;

      case ActionMode?.TAGCAMERA:
        // open camera mapping pop over
        this.showPopover = true;
        this.entityForCameraMapping = entity;
        break;

      case ActionMode?.UNTAGCAMERA:
        // remove tagged camera
        this._unTagCamera(entity);
        break;
    }
  }

  /**
   * Views entity location
   * @param entityId
   */
  private _viewEntityLocation(entityId: string) {
    this._viewEntityLocationFlagSubscription =
      this._viewEntityLocationFlag.subscribe({
        next: (flag: boolean) => {
          if (flag && entityId) {
            const buttonContent = this.planEntityButtonList?.find(
              (button) => button?.action === ActionMode?.VIEW
            );
            const planEntityList = this.planEntityListGroup?.find(
              (entityList) => entityList?.listGroup === ZonePlanGroup.MAPPED
            );
            const entity = planEntityList?.list?.find(
              (entity) => entity?.entityId === entityId
            );
            // View entity location on plan
            this._setPlanEntityButtonAction(
              buttonContent,
              entity,
              planEntityList
            );
          }
        },
      });
  }

  /**
   * Sets visible category
   */
  private _setVisibleCategory() {
    this._visibleCategoryList =
      this._tenantGenericConfig?.tracker?.floorPlan?.visibleCategory?.reduce(
        (accumulator: ZonePlanVisibleCategoryModel, categoryConfig) => {
          return {
            ...accumulator,
            [categoryConfig?.category]: {
              subCategoryTracked: categoryConfig?.subCategoryTracked,
              showHealthStatus: categoryConfig?.showHealthStatus,
            },
          };
        },
        {} as ZonePlanVisibleCategoryModel
      ) ?? ({} as ZonePlanVisibleCategoryModel);
  }

  /**
   * Sets view switcher category
   */
  private _setViewSwitcherCategory() {
    // set default view switcher options
    this._setDefaultViewSwitcherOption();
    // First priority from tenantGenericConfig
    let categoryList = Object.keys(this._visibleCategoryList);
    if (!categoryList?.length) {
      // Last priority from planEntityList
      categoryList = [...this.planEntityList]?.map(
        (entityContent) => entityContent?.category
      );
    }
    // Remove duplicates
    categoryList = [...new Set(categoryList)];
    categoryList?.forEach((category) => {
      const entityType = 2;
      const categoryIconData = this.getIconData(entityType, category);
      this.viewSwitcherOptions.push({
        tooltip: category,
        dynamicType: category,
        iconUrl: categoryIconData?.imgUrl + this.blobSasToken,
        active: false,
      });
    });
  }

  /**
   * Sets plot content state by category
   * @param state
   * @param category
   */
  private _setPlotContentStateByCategory(state: boolean, category: string) {
    // Set plot content state
    this.plotContent = this.plotContent?.map(
      (planEntity: ZonePlanEntityModel) => {
        return {
          ...planEntity,
          active: planEntity?.category === category ? !state : state,
        };
      }
    );
  }

  /**
   * Sets view switcher option state
   * @param state
   * @param [dynamicType]
   */
  private _setViewSwitcherOptionState(
    state: boolean,
    dynamicType: string = null
  ) {
    // Set view switcher pointer events
    this.viewSwitcherDisabled = dynamicType ? false : true;
    // Set view switcher state
    this.viewSwitcherOptions = this.viewSwitcherOptions?.map(
      (option: ViewSwitcherOptionModel) => {
        return {
          ...option,
          active: option?.dynamicType === dynamicType ? !state : state,
        };
      }
    );
  }

  /**
   * Sets default view switcher option
   */
  private _setDefaultViewSwitcherOption() {
    this.viewSwitcherOptions = [
      {
        tooltip: Messages.VIEW_SWITCHER_VIEW_ALL,
        dynamicType: ViewType.VIEW_ALL,
        icon: 'icon-viewswitcher-view-all.svg',
        active: true,
      },
    ];
  }

  /**
   * Sets relational entity drawer visibility
   * @param option
   */
  private _setEntityDrawerVisibility(option: VisibilityType) {
    switch (option) {
      case Visibility.OPEN:
        this.entityDrawerRef?.open();
        break;
      case Visibility.CLOSE:
        this.entityDrawerRef?.close();
        break;
    }
  }

  /**
   * Sets dimension
   */
  private _setDimension() {
    this.entityDrawerDimension = {
      width: this.zoneGroupContainerRef?.nativeElement?.clientWidth,
      height: this.zoneGroupContainerRef?.nativeElement?.clientHeight,
    };
    this.listAreaDimension = {
      width: null,
      height: this.planImageRef?.nativeElement?.clientHeight,
    };
    this._changeDetectorRef.detectChanges();
  }

  /**
   * Gets aggregation category list
   * @returns aggregation category list
   */
  private _getAggregationCategoryList(): ZonePlanAggregationCategoryModel[] {
    return Object.keys(this._visibleCategoryList)?.map((category: string) => {
      return {
        trackerCategory: category,
        entityCategory:
          this._visibleCategoryList?.[category]?.subCategoryTracked,
      } as ZonePlanAggregationCategoryModel;
    });
  }

  ngAfterViewInit(): void {
    // Initialize canvas after view init
    setTimeout(() => {
      this.initializeImageLoadHandler();
    }, 0);

    // Handle window resize for responsiveness
    window.addEventListener('resize', this.onWindowResize.bind(this));
    document.addEventListener('mousemove', this.onDocumentMouseMove.bind(this));
    document.addEventListener('mouseup', this.onDocumentMouseUp.bind(this));
    document.addEventListener('fullscreenchange', this.onFullscreenChange.bind(this));
    document.addEventListener('fullscreenchange', this.handleFullscreenChange.bind(this));
  }

  handleFullscreenChange() {
  const container = document.querySelector('.cdk-overlay-container');
  const fullscreenEl = document.fullscreenElement;

  if (!container) return;

  if (fullscreenEl) {
    fullscreenEl.appendChild(container);
  } else {
    document.body.appendChild(container);
  }
}

  onFullscreenChange(): void {
  if (!document.fullscreenElement) {
    this.isFullScreen = false;
    this.expand.emit(false);
    setTimeout(() => {
      this.setupCanvas();
      this._setPlotConfig();
      this.updateInfoIconPositions();
    }, 200);
  }
}

  /**
   * Set up explicit image load handler
   */
  private initializeImageLoadHandler(): void {
    if (!this.planImageRef?.nativeElement) {
      setTimeout(() => this.initializeImageLoadHandler(), 100);
      return;
    }
    const img = this.planImageRef.nativeElement;
    if (img.complete) {
      this.handleImageLoaded();
    } else {
      img.onload = () => {
        this.handleImageLoaded();
      };
    }
  }

  /**
   * Handle image loaded event
   */
  private handleImageLoaded(): void {
    const img = this.planImageRef.nativeElement;
    this.imageNaturalWidth = img.naturalWidth || 0;
    this.imageNaturalHeight = img.naturalHeight || 0;
    const imgRect = img.getBoundingClientRect();
    this.displayWidth = imgRect.width;
    this.displayHeight = imgRect.height;
    this._setBasicPlotConfig();
    this._setPlotConfig();
    this.setupCanvas();
    this.updateInfoIconPositions();
    this._changeDetectorRef.detectChanges();
  }

  setupCanvas(): void {
    if (
      !this.planImageRef?.nativeElement ||
      !this.polygonCanvas?.nativeElement
    ) {
      return;
    }
    // Get the canvas element and context
    const canvas = this.polygonCanvas.nativeElement;
    this.ctx = canvas.getContext('2d')!;
    const img = this.planImageRef.nativeElement;
    // Store natural image dimensions
    this.imageNaturalWidth = img.naturalWidth || img.width;
    this.imageNaturalHeight = img.naturalHeight || img.height;
    // Get the actual displayed dimensions of the image
    const imgRect = img.getBoundingClientRect();
    this.displayWidth = imgRect.width;
    this.displayHeight = imgRect.height;
    // Set canvas size to match the displayed image size
    canvas.width = this.displayWidth;
    canvas.height = this.displayHeight;
    // Position the canvas to exactly overlay the image
    canvas.style.position = 'absolute';
    canvas.style.top = `${
      imgRect.top - (img.parentElement?.getBoundingClientRect().top || 0)
    }px`;
    canvas.style.left = `${
      imgRect.left - (img.parentElement?.getBoundingClientRect().left || 0)
    }px`;
    // Redraw all polygons with updated dimensions
    this.redrawPolygons();
  }

  isPolygonInProgress = false; // Track if we're in the middle of the polygon saving workflow
  pendingPolygonId: string | null = null; // Track ID of polygon waiting for save/cancel decision

  toggleDrawingMode(): void {
    // First check if we have a polygon in progress that needs resolution
    if (
      this.isPolygonInProgress ||
      this.plotAreaMode === ActionMode.ADD ||
      this.plotAreaMode === ActionMode.EDIT
    ) {
      return;
    }
    // Toggle drawing mode state
    this.drawingMode = !this.drawingMode;
    this.selectedPolygonId = null;
    if (!this.drawingMode) {
      // Cancel current polygon if any
      this.currentPolygon = [];
      this.isDrawing = false;
    }

    // Redraw polygons
    this.redrawPolygons();
  }

  // Handle mouse down on canvas
  onCanvasMouseDown(event: MouseEvent): void {
    // First check if a polygon is in progress
    if (this.isPolygonInProgress && this.drawingMode) {
      return;
    }
    const rect = this.polygonCanvas.nativeElement.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    if (this.drawingMode) {
      // Start or continue drawing a polygon
      if (!this.isDrawing) {
        this.isDrawing = true;
        this.currentPolygon = [{ x, y }];
      } else {
        // Check if close to starting point to close the polygon
        const startPoint = this.currentPolygon[0];
        const distance = Math.sqrt(
          Math.pow(x - startPoint.x, 2) + Math.pow(y - startPoint.y, 2)
        );
        if (this.currentPolygon.length > 2 && distance < 20) {
          if (this.checkPolygonOverlap(this.currentPolygon)) {
            this._notifyUser();
            return; // Don't close the polygon
          }
          this.completePolygon();
        } else {
          const lastPoint = this.currentPolygon[this.currentPolygon.length - 1];
          const newPoint = { x, y };
          if (this.checkLineIntersectsPolygons(lastPoint, newPoint)) {
            this._notifyUser();
            return;
          }
          this.currentPolygon.push(newPoint);
        }
      }
    } else {
      // Select polygon if not in drawing mode
      this.selectPolygonAtPoint(x, y);
    }
    // Redraw everything
    this.redrawPolygons();
  }
  // Handle double click to complete polygon
  onCanvasDoubleClick(): void {
    if (this.isDrawing && this.currentPolygon.length > 2) {
      if (this.checkPolygonOverlap(this.currentPolygon)) {
        this._notifyUser();
        return;
      }
      // Calculate percentage coordinates
      const pointsPercent = this.currentPolygon.map((point) => ({
        x: point.x / this.displayWidth,
        y: point.y / this.displayHeight,
      }));
      const newPolygonId = 'polygon_' + Date.now();
      const newPolygon = {
        pointsPercent,
        id: newPolygonId,
        isPending: true, // Add a flag to mark as pending
      };
      this.polygons.push(newPolygon);
      this.pendingPolygonId = newPolygonId;
      // Clear current polygon drawing state
      this.currentPolygon = [];
      this.isDrawing = false;
      // Set the polygon workflow as in progress, preventing new drawings
      this.isPolygonInProgress = true;
      // Redraw with the new pending polygon
      this.redrawPolygons();
    }
  }

  // Complete current polygon and store as percentage coordinates
  completePolygon(): void {
    if (this.currentPolygon.length < 3) {
      return; // Need at least 3 points for a polygon
    }
    if (!this.validatePolygonComplete(this.currentPolygon)) {
      this._notifyUser();
      return; // Don't complete the polygon if validation fails
    }
    // Convert current polygon points to percentage-based coordinates
    const pointsPercent = this.currentPolygon.map((point) => ({
      x: point.x / this.displayWidth,
      y: point.y / this.displayHeight,
    }));
    const newPolygonId = 'polygon_' + Date.now();
    // Add to polygons array as a pending polygon
    const newPolygon = {
      pointsPercent,
      id: newPolygonId,
      isPending: true, // Add a flag to mark as pending
    };
    this.polygons.push(newPolygon);
    this.pendingPolygonId = newPolygonId;
    // Clear current polygon drawing state
    this.currentPolygon = [];
    this.isDrawing = false;
    // Set the polygon workflow as in progress, preventing new drawings
    this.isPolygonInProgress = true;
    // Redraw with the new pending polygon
    this.redrawPolygons();
  }

  // Convert percentage coordinates to pixel coordinates for drawing
  percentToPixelCoords(
    percentPoints: { x: number; y: number }[]
  ): { x: number; y: number }[] {
    return percentPoints.map((point) => ({
      x: point.x * this.displayWidth,
      y: point.y * this.displayHeight,
    }));
  }

  updateInfoIconPositions(): void {
    // Call this whenever zoom changes
    this.redrawPolygons();
    // Force a detection of changes to update the DOM
    if (this._changeDetectorRef) {
      this._changeDetectorRef.detectChanges();
    }
  }

  // Select polygon at point
  selectPolygonAtPoint(x: number, y: number): void {
    this.selectedPolygonId = null;
    // Check each polygon in reverse order (top-most first)
    for (let i = this.polygons.length - 1; i >= 0; i--) {
      const pixelPoints = this.percentToPixelCoords(
        this.polygons[i].pointsPercent
      );
      if (this.isPointInPolygon({ x, y }, pixelPoints)) {
        this.selectedPolygonId = this.polygons[i].id;
        break;
      }
    }
  }

  // Delete selected polygon
  deleteSelectedPolygon(worktable: any): void {
    if (this.selectedPolygonId) {
      if (
        this._checkZoneHasMarkers(this.selectedPolygonId, this.planEntityList)
      ) {
        this._apiService.toasterService({
          type: 'warning',
          msg: 'This Worktable contains Workbenches/Cameras. Please remove them before deleting the Worktable.',
        });
      } else {
        const dialogConfig: MatDialogConfig<DialogModel> = {
          data: {
            title: Messages?.ZONE_PLAN_DIALOG_DELETE_WORKTABLE,
            content:
              Messages?.ZONE_PLAN_DIALOG_DELETE_WORKTABLE_CONTENT +
              ' ' +
              worktable?.name +
              '?',
            actions: [
              {
                label: Messages?.BUTTON_LABEL_CANCEL,
                type: ButtonType?.BUTTON,
              },
              {
                label: Messages?.BUTTON_LABEL_SUBMIT,
                type: ButtonType?.BUTTON,
                value: true,
                color: ButtonColor.PRIMARY,
              },
            ],
          },
        };
        this._afterClosedSubscription = this._dialogService
          ?.showDialog(dialogConfig)
          ?.afterClosed()
          ?.subscribe({
            next: (confirmed: boolean) => {
              if (!confirmed) {
                return;
              }
              this._deleteZoneData(this.selectedPolygonId);
            },
            error: (error) => {
              console.error('Dialog interaction error:', error);
            },
          });
      }
    } else {
      this._apiService.toasterService({
        type: 'error',
        msg: 'Worktable Id not available',
      });
    }
  }

  /**
   * Function to check if a zone contains any markers/entities before deletion
   * @param zoneId - The ID of the zone to be deleted
   * @param planEntities - The list of entities/markers from the API response
   * @returns {boolean} - Returns true if the zone has markers, false otherwise
   */
  private _checkZoneHasMarkers(zoneId: string, planEntities: any[]): boolean {
    if (!zoneId || !planEntities?.length) {
      return false;
    }
    const entitiesInZone = planEntities.filter(
      (entity) =>
        entity.zones &&
        Array.isArray(entity.zones) &&
        entity.zones.includes(zoneId)
    );
    return entitiesInZone.length > 0;
  }

  // Redraw all polygons
  redrawPolygons(): void {
    if (!this.ctx || !this.polygonCanvas) return;
    // Clear canvas
    this.ctx.clearRect(0, 0, this.displayWidth, this.displayHeight);
    // Draw completed polygons
    this.polygons.forEach((polygon) => {
      // Convert percentage coordinates to pixel coordinates for drawing
      const pixelPoints = this.percentToPixelCoords(polygon.pointsPercent);
      this.ctx.beginPath();
      // Move to the first point
      if (pixelPoints.length > 0) {
        this.ctx.moveTo(pixelPoints[0].x, pixelPoints[0].y);
        // Draw lines to each point
        for (let i = 1; i < pixelPoints.length; i++) {
          this.ctx.lineTo(pixelPoints[i].x, pixelPoints[i].y);
        }
        // Close the path
        this.ctx.closePath();
        // Set styles based on selection and pending state
        if (polygon.id === this.selectedPolygonId) {
          this.ctx.fillStyle = 'rgba(0, 128, 255, 0.3)';
          this.ctx.strokeStyle = 'blue';
          this.ctx.setLineDash([5, 5]);
        } else if (polygon.isPending) {
          // Use a different style for pending polygons
          this.ctx.fillStyle = 'rgba(255, 165, 0, 0.3)'; // Orange with transparency
          this.ctx.strokeStyle = 'orange';
          this.ctx.setLineDash([5, 5]); // Dashed line for pending polygons
        } else {
          this.ctx.fillStyle = 'rgba(0, 128, 255, 0.3)';
          this.ctx.strokeStyle = 'blue';
          this.ctx.setLineDash([5, 5]); // Reset to solid line
        }
        this.ctx.lineWidth = 2;
        this.ctx.fill();
        this.ctx.stroke();
        this.ctx.setLineDash([]); // Reset to solid line after drawing
      }
    });
    // Draw current polygon being created
    if (this.isDrawing && this.currentPolygon.length > 0) {
      this.ctx.beginPath();
      this.ctx.moveTo(this.currentPolygon[0].x, this.currentPolygon[0].y);
      for (let i = 1; i < this.currentPolygon.length; i++) {
        this.ctx.lineTo(this.currentPolygon[i].x, this.currentPolygon[i].y);
      }
      this.ctx.strokeStyle = 'green';
      this.ctx.lineWidth = 2;
      this.ctx.stroke();
      // Draw points
      this.currentPolygon.forEach((point) => {
        this.ctx.beginPath();
        this.ctx.arc(point.x, point.y, 4, 0, 2 * Math.PI);
        this.ctx.fillStyle = 'green';
        this.ctx.fill();
      });
    }
  }

  // Export polygons data in percentage format (already stored that way)
  exportPolygons(): any[] {
    return this.polygons.map((polygon) => ({
      id: polygon.id,
      points: polygon.pointsPercent,
    }));
  }
  // Import polygons data (percentage format)
  importPolygons(polygonData: any[]): void {
    this.polygons = polygonData.map((data) => ({
      id: data.id || 'polygon_' + Date.now(),
      pointsPercent: data.points,
    }));
    this.redrawPolygons();
  }

  // Save polygons to backend (implement your API call here)
  savePolygons(): void {
    if (this.newZoneName.trim()) {
      const polygonsData = this.exportPolygons();
      this._createZone(this.newZoneName.trim(), polygonsData);
      // Reset the workflow state
      this.pendingPolygonId = null;
      this.isPolygonInProgress = false;
      this.drawingMode = false;
      // Redraw
      this.redrawPolygons();
    }
  }

  resetZoneName(): void {
    this.showZoneNamePopover = false;
    this.newZoneName = '';
  }

  isPolygonHovered(polygonId: string): boolean {
    const polygon = this.polygons.find((p) => p.id === polygonId);
    if (!polygon) {
      return false; // Polygon doesn't exist
    }
    if (polygon.isPending) {
      return false;
    }
    return (
      this.selectedPolygonId === polygonId ||
      this.hoveredPolygonId === polygonId ||
      this.activeInfoPolygonId === polygonId
    );
  }

  selectPolygonById(event: any): void {
    this.selectedPolygonId = event?._id ?? null;
  }

  cancelDrawing(): void {
    this.currentPolygon = [];
    this.isDrawing = false;
    // If there's a pending polygon, remove it
    if (this.pendingPolygonId) {
      this.polygons = this.polygons.filter(
        (p) => p.id !== this.pendingPolygonId
      );
      this.pendingPolygonId = null;
    }
    this.isPolygonInProgress = false;
    this.drawingMode = false;
    this.selectedPolygonId = null;
    this.redrawPolygons();
    this._fetchZonesData();
  }

  isPolygonSaved(polygonId: string): boolean {
    return this.polygons.some((p) => p.id === polygonId);
  }

  completeAndSave(): void {
    if (this.isPolygonInProgress) {
      if (this.pendingPolygonId) {
        const pendingPolygonIndex = this.polygons.findIndex(
          (p) => p.id === this.pendingPolygonId
        );
        if (pendingPolygonIndex !== -1) {
          this.polygons[pendingPolygonIndex].isPending = false;
          this.showZoneNamePopover = true;
        }
      } else if (this.isDrawing && this.currentPolygon.length > 2) {
        this.completePolygon();
        this.completeAndSave();
      } else {
        this.drawingMode = false;
        this.isPolygonInProgress = false;
        this.redrawPolygons();
      }
    }
  }

  onCanvasMouseMove(event: MouseEvent): void {
    if (!this.ctx || this.isDrawing) return;
    const rect = this.polygonCanvas.nativeElement.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    let foundHover = false;
    for (let i = this.polygons.length - 1; i >= 0; i--) {
      const pixelPoints = this.percentToPixelCoords(
        this.polygons[i].pointsPercent
      );
      if (this.isPointInPolygon({ x, y }, pixelPoints)) {
        this.hoveredPolygonId = this.polygons[i].id;
        foundHover = true;
        break;
      }
    }
    if (!foundHover) {
      this.hoveredPolygonId = null;
    }
  }

  toggleInfoPopover(polygonId: string, event: MouseEvent): void {
    event.stopPropagation();
    if (this.activeInfoPolygonId === polygonId) {
      this.activeInfoPolygonId = null;
    } else {
      this.activeInfoPolygonId = polygonId;
    }
  }

  importZoneData(zoneData: any[]): void {
    if (!zoneData || !zoneData.length) {
      this.polygons = [];
      this.redrawPolygons();
      return;
    }
    const polygonsToImport: any[] = [];
    zoneData.forEach((zone) => {
      if (
        zone?.floorPlan?.geometry?.type === 'Polygon' &&
        zone?.floorPlan?.geometry?.coordinates?.length
      ) {
        const polygon = {
          id: zone._id || 'polygon_' + Date.now(),
          pointsPercent: zone.floorPlan.geometry.coordinates,
          name: zone.name,
          nature: zone.nature,
        };
        polygonsToImport.push(polygon);
      }
    });
    if (polygonsToImport.length) {
      this.polygons = [...this.polygons, ...polygonsToImport];
      this.redrawPolygons();
    }
  }

  /**
   * Creates a new zone and handles the response
   * @param name - zone name
   * @param coordinates - plogyom coordinates
   *
   */
  private _createZone(zoneName: string, coordinates: any): void {
    const zoneDetails = {
      order: 99,
      name: zoneName,
      level: 100,
      centroid: null,
      geoDrawButton: {
        name: 'Draw New Zone',
        id: 1,
      },
      nature: 'WTB',
      natureDescription: 'Worktable',
      floorPlan: {
        image: null,
        geometry: {
          type: 'Polygon',
          coordinates: coordinates?.[coordinates?.length - 1]?.points ?? [],
        },
        properties: {
          strokeColor: '#55E6C1',
          strokeOpacity: 10,
          strokeWeight: 10,
          fillColor: '#000',
          fillOpacity: 10,
          labelColor: '#000',
        },
      },
      properties: {
        destination: null,
      },
      attribute: {
        riskType: 'low',
        altitude: 0,
        maxPressure: 0,
        minPressure: 0,
        floorHeight: 0,
      },
      geoFence: this.zone?.geoFence ?? null,
      restrictedAccess: {
        status: false,
        start: '00:00',
        timeZone: '+05:30',
        end: '23:59',
      },
      floorPlanGeo: [
        {
          order: 'NE',
          lat: '',
          lng: '',
        },
        {
          order: 'NW',
          lat: '',
          lng: '',
        },
        {
          order: 'SE',
          lat: '',
          lng: '',
        },
        {
          order: 'SW',
          lat: '',
          lng: '',
        },
      ],
      applyToChildren: false,
      hierarchy: this.zone?._id ?? null,
    };
    this._createNewZoneSubscription = this._apiService
      .createNewZone(
        this._apiService.getSiteId(),
        'floorzone',
        null,
        zoneDetails
      )
      .pipe(
        finalize(() => {}),
        takeUntil(this._destroy$)
      )
      .subscribe({
        next: (result: any) => {
          if (result?.status && result?.response) {
            this._apiService.toasterService({
              type: 'success',
              msg: result.response.message,
            });
            this.resetZoneName();
            this._fetchZonesData();
          } else {
            this._apiService.toasterService({
              type: Messages.ERROR,
              msg: result['response']['message'],
            });
            this.newZoneName = '';
          }
        },
        error: () => {
          this._apiService.toasterService({
            type: Messages.ERROR,
            msg: 'An error occurred while creating the zone',
          });
          this.newZoneName = '';
        },
      });
  }

  /**
   * Deletes a zone by its ID and handles the response
   * @param zoneId - The ID of the zone to delete
   */
  private _deleteZoneData(zoneId: string): void {
    this._deleteZoneSubscription = this._apiService
      .deleteZone(zoneId)
      .pipe(
        catchError((error) => {
          this._apiService.toasterService({
            type: Messages.ERROR,
            msg: error?.response?.message || 'Failed to delete zone',
          });
          return EMPTY;
        })
      )
      .subscribe({
        next: (response: any) => {
          if (response.status) {
            this._apiService.toasterService({
              type: Messages.SUCCESS,
              msg: response.response.message,
            });
            this._fetchZonesData();
          } else {
            this._apiService.toasterService({
              type: Messages.ERROR,
              msg: response.response.message,
            });
          }
        },
      });
  }

  /**
   * Fetches zone data from the API service
   */
  private _fetchZonesData(isPlanEntities: boolean = false): void {
    const requestPayload: any = {
      page: 0,
      siteId: this._apiService.getSiteId(),
      classType: 'floorzone',
      sortBy: null,
      hierarchyId: this.zone?._id ?? null,
    };

    this._getZonesDataSubscription = this._zoneService
      .getZonesData(requestPayload)
      .pipe(
        catchError((error) => {
          this._apiService.toasterService({
            type: Messages.ERROR,
            msg: error?.response?.message || 'Failed to fetch zone data',
          });
          return EMPTY;
        })
      )
      .subscribe({
        next: (response: any) => {
          if (response?.response?.data) {
            this.zoneData = response.response.data;
            this.polygons = [];
            this.importZoneData(this.zoneData);
            if (isPlanEntities) this._getPlanEntities();
            const floorZoneIds = response?.response?.data?.map(
              (item) => item._id
            );
            this._dataService.broadcast('floorZone', floorZoneIds);
          } else {
            this._apiService.toasterService({
              type: Messages.ERROR,
              msg: response.response?.message || 'Error retrieving zone data',
            });
          }
        },
      });
  }

  /**
   * Gets the status name for the given tracked state
   * @param trackedState - The tracked state value
   * @returns The name of the status from planogramSummaryData
   */
  public getStatusName(trackedState: string | null): string {
    return this._planogramSharedService.getStatusName(trackedState);
  }

  /**
   * Gets the status color for the given status value
   * @param status - The status value (e.g., "1.0", "2.0", etc.)
   * @returns The status color from statusMapping or default color
   */
  public getStatusColor(status: string | null | undefined): string {
    if (!status) {
      return '#888888'; // Default gray color for unknown status
    }
    const statusItem = this._statusMappingArray?.find(
      (item: any) => item?.value === status
    );
    return statusItem?.statusColor || '#888888';
  }

  /**
   * Filters attributes based on the configured attributeList
   * @param attributes - The attributes array from the entity
   * @returns Filtered attributes array with mapped attributeNames from attributeList
   */
  public getFilteredAttributes(attributes: any[]): any[] {
    if (!attributes || !this.attributeList || this.attributeList.length === 0) {
      return attributes || [];
    }

    // Create a map of attribute names to their display names from attributeList
    const attributeNameMap = new Map(
      this.attributeList.map((item: any) => [item?.attribute, item?.attributeName])
    );

    // Filter attributes that are in the allowed list and map their display names
    return attributes
      .filter((attr: any) => attributeNameMap.has(attr?.attributeName))
      .map((attr: any) => ({
        ...attr,
        displayAttributeName: attributeNameMap.get(attr?.attributeName) || attr?.attributeName
      }));
  }

  /**
   * Gets all configured attributes with values from plot attributes
   * Shows all fields from attributeList, with values from plot.attributes or 'NA' if not found
   * @param attributes - The attributes array from the entity
   * @returns Array of all configured attributes with their values or 'NA'
   */
    public getAllConfiguredAttributes(attributes: any[]): any[] {
      if (!this.attributeList?.length) return [];
    
      const attrArray = attributes || [];
    
      return this.attributeList
        .map((item: any) => {
        
          // GROUP HANDLING
          if (item.type === 'group') {
            const keys = attrArray.filter(a =>
              a.attributeName === item.groupBy
            );
            const values = attrArray.filter(a =>
              a.attributeName === item.mapValueFrom
            );
          
            if (!keys.length && !values.length) return null;
          
            const children = keys.map((k, index) => ({
              displayAttributeName: k.attributeValue ?? 'NA',
              attributeValue: values[index]?.attributeValue ?? 'NA',
              attribute: item.mapValueFrom,
              isGroupChild: true,
              isRecentTimestamp: values[index]?.isRecentTimestamp ?? false,
            }));    

            return {
              displayAttributeName: item.attributeName,
              isGroup: true,
              children,
            };
          }   

          // NORMAL ATTRIBUTE
          const match = attrArray.find(a =>
            a.attributeName?.toLowerCase() === item.attribute?.toLowerCase()
          );
          if (!match) return null;    

          return {
            attribute: item.attribute,
            displayAttributeName: item.attributeName,
            attributeValue: match.attributeValue ?? 'NA',
            isGroup: false,
            isRecentTimestamp: match.isRecentTimestamp ?? false,
          };
        })
        .filter(Boolean);
    }

  /**
   * Checks if there are any grouped attributes
   * @param attributes - The attributes array from the entity
   * @returns True if grouped attributes exist, false otherwise
   */
  public hasGroupedAttributes(attributes: any[]): boolean {
    const configuredAttributes = this.getAllConfiguredAttributes(attributes);
    return configuredAttributes?.some((attr: any) => attr?.isGroup) ?? false;
  }

  /**
   * Gets normal (non-grouped) attributes only
   * @param attributes - The attributes array from the entity
   * @returns Array of normal attributes
   */
  public getNormalAttributes(attributes: any[]): any[] {
    return this.getAllConfiguredAttributes(attributes)?.filter((attr: any) => !attr?.isGroup) ?? [];
  }

  /**
   * Gets grouped attributes only
   * @param attributes - The attributes array from the entity
   * @returns Array of grouped attributes
   */
  public getGroupedAttributes(attributes: any[]): any[] {
    return this.getAllConfiguredAttributes(attributes)?.filter((attr: any) => attr?.isGroup) ?? [];
  }

  /**
   * Gets the display value for an attribute (handles timestamp check)
   * @param attributeValue - The attribute value
   * @param isRecentTimestamp - Whether the timestamp is recent
   * @returns The attribute value or 'NA'
   */
  public getAttributeDisplayValue(attributeValue: any, isRecentTimestamp: boolean): string {
    return (attributeValue && isRecentTimestamp) ? attributeValue : 'NA';
  }

  /**
   * Gets the status color for grouped attribute children
   * @param attributeValue - The attribute value
   * @param isRecentTimestamp - Whether the timestamp is recent
   * @returns The status color
   */
  public getGroupedAttributeStatusColor(attributeValue: any, isRecentTimestamp: boolean): string {
    return this.getStatusColor(isRecentTimestamp ? attributeValue : null);
  }

  /**
   * Checks if there are any normal (non-grouped) attributes
   * @param attributes - The attributes array from the entity
   * @returns True if normal attributes exist, false otherwise
   */
  public hasNormalAttributes(attributes: any[]): boolean {
    const configuredAttributes = this.getAllConfiguredAttributes(attributes);
    return configuredAttributes?.some((attr: any) => !attr?.isGroup) ?? false;
  }

  /**
   * Checks if a timestamp falls within the last N seconds
   * @param timestamp - The timestamp to check (ISO string or Date)
   * @param seconds - Number of seconds to check within
   * @returns True if timestamp is within last N seconds, false otherwise
   */
  private _isTimestampWithinLastSeconds(timestamp: any, seconds: number): boolean {
    if (!timestamp) {
      return false;
    }
    
    try {
      const timestampDate = new Date(timestamp);
      const currentTime = new Date();
      const timeDifference = currentTime.getTime() - timestampDate.getTime();
      const secondsInMillis = seconds * 1000;
      
      return timeDifference >= 0 && timeDifference <= secondsInMillis;
    } catch (error) {
      console.error('Error checking timestamp:', error);
      return false;
    }
  }

  /**
   * Updates plotContent with additional information from planogramEntityList
   */
  private _updatePlotContentFromEntityList() {
    if (!this.planogramEntityList || !this.plotContent) {
      return;
    }
    const entityMap = new Map();
    this.planogramEntityList.forEach((entity) => {
      entityMap.set(entity.entityId, entity);
    });

    // Update plotContent with additional information from planogramEntityList
    this.plotContent = this.plotContent.map(
      (plotEntity: ZonePlanEntityModel) => {
        const matchingEntity = entityMap.get(plotEntity.entityId);
        if (matchingEntity) {
          return {
            ...plotEntity,
            trackedState: matchingEntity?.trackedState ?? null,
            tisDisplayValue: matchingEntity?.tisDisplayValue ?? 'NA',
            sourceTimeDisplayValue:
              matchingEntity?.sourceTimeDisplayValue ?? 'NA',
            entityName: matchingEntity?.entityName || plotEntity.entityName,
            tag: matchingEntity?.tags?.[0] ?? 'NA',
          };
        }
        return plotEntity;
      }
    );
  }

  /**
   * Determines if a camera button should be visible for a given entity
   * @param entity - The entity to check
   * @param buttonAction - The action type of the button
   * @param buttonGroup - The button group
   * @returns Whether the button should be visible
   */
  isButtonVisible(
    entity: any,
    buttonAction: string,
    entityList: ZonePlanEntityGroupModel
  ): boolean {
    if (
      !this.categoryList?.['workBench'] ||
      entityList?.title === Messages?.ZONE_PLAN_MAPPED_LABEL
    ) {
      return false;
    }
    const isWorkbenchCategory =
      entity?.category === this.categoryList['workBench'];
    if (!isWorkbenchCategory) {
      return false;
    }
    if (entityList?.buttonGroup === ZonePlanButtonGroup.ADD) {
      return false;
    }
    switch (buttonAction) {
      case ActionMode.TAGCAMERA:
        return !entity?.deviceOid;
      case ActionMode.UNTAGCAMERA:
        return Boolean(entity?.deviceOid);
      default:
        return false;
    }
  }

  handleCancel(): void {
    this.showPopover = false;
  }

  /**
   * Handles the allocation of a device to an entity
   * @param {any} device - The device object to be allocated
   * @returns {void} No return value
   */
  handleAllocateDevice(device: any): void {
    if (!device) {
      this.showPopover = false;
      return;
    }
    const attributeList = this._apiService.getConfiguration('bluePrint');
    if (
      (!attributeList || !Object?.keys(attributeList)?.length) &&
      (!this.categoryList?.['workBench'] ||
        !Object?.keys(attributeList)?.some(
          (key) => key === this.categoryList?.['workBench']
        ) ||
        !Object?.keys(attributeList[this.categoryList?.['workBench']])?.length)
    ) {
      this._apiService.toasterService({
        type: 'warning',
        msg: 'Blue Print/Work Bench configuration not available',
      });
      this.showPopover = false;
      return;
    }
    const entityDeviceAllocationPayload = {
      siteId: this._apiService.getSiteId(),
      deviceId: device?._id,
      deviceType: null,
      currentTime: this._datePipe.transform(new Date(), 'yyyy-MM-ddTHH:mm:ssZ'),
      entityId: this.entityForCameraMapping?.entityId,
      attribute: attributeList[this.categoryList?.['workBench']],
    };
    this._entityDeviceAllocationSubscription?.unsubscribe();
    this._entityDeviceAllocationSubscription = this._apiService
      .entityDeviceAllocation(entityDeviceAllocationPayload)
      .subscribe({
        next: (response: any) => {
          const isSuccess = !!response?.status;
          this._apiService.toasterService({
            type: isSuccess ? 'success' : 'error',
            msg:
              response?.response?.message ||
              (isSuccess
                ? 'Device allocated successfully'
                : 'Failed to allocate device'),
          });
          this.entityForCameraMapping = null;
          if (isSuccess) {
            this._dataService.broadcast(
              DataBroadcast.TRACKER_ENTITY_LOCATION_CHANGED
            );
          }
          this.showPopover = false;
        },
        error: (error) => {
          console.error('Device allocation error:', error);
          this._apiService.toasterService({
            type: 'error',
            msg: 'Error occurred while allocating device',
          });
          this.entityForCameraMapping = null;
          this.showPopover = false;
        },
      });
  }

  onContainerMouseDown(event: MouseEvent): void {
    if (event.button !== 0) return;
    if (!this.isDragging) {
      this.onPlanImageClick(event);
    }
    event.preventDefault();
    this.isDragging = true;
    this.startX = event.clientX;
    this.startY = event.clientY;
    this.lastX = this.pointX;
    this.lastY = this.pointY;
    if (this.planContainer) {
      this.planContainer.nativeElement.style.cursor = 'grabbing';
    }
  }

  onDocumentMouseMove(event: MouseEvent): void {
    if (!this.isDragging || this.drawingMode) return;
    const dx = (event.clientX - this.startX) / this.scale;
    const dy = (event.clientY - this.startY) / this.scale;
    this.pointX = this.lastX + dx;
    this.pointY = this.lastY + dy;
  }

  onDocumentMouseUp(): void {
    if (!this.isDragging) return;
    this.isDragging = false;
    if (this.planContainer) {
      this.planContainer.nativeElement.style.cursor = 'grab';
    }
  }

  zoomIn(): void {
    if (!this.planContainer) return;
    const container = this.planContainer.nativeElement;
    const rect = container.getBoundingClientRect();
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const contentX = centerX / this.scale - this.pointX;
    const contentY = centerY / this.scale - this.pointY;
    const newScale = Math.min(this.maxScale, this.scale + this.zoomStep);
    if (newScale !== this.scale) {
      this.scale = newScale;
      this.pointX = -(contentX - centerX / this.scale);
      this.pointY = -(contentY - centerY / this.scale);
      this.showZoomControlsTemporarily();
    }
  }

  zoomOut(): void {
    if (!this.planContainer) return;
    const container = this.planContainer.nativeElement;
    const rect = container.getBoundingClientRect();
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const contentX = centerX / this.scale - this.pointX;
    const contentY = centerY / this.scale - this.pointY;
    const newScale = Math.max(this.minScale, this.scale - this.zoomStep);
    if (newScale !== this.scale) {
      this.scale = newScale;
      this.pointX = -(contentX - centerX / this.scale);
      this.pointY = -(contentY - centerY / this.scale);
      this.showZoomControlsTemporarily();
    }
  }

  resetZoom(): void {
    this.scale = 1;
    this.pointX = 0;
    this.pointY = 0;
    this.showZoomControlsTemporarily();
  }

  showZoomControlsTemporarily(): void {
    if (this.zoomControlsTimeout) {
      clearTimeout(this.zoomControlsTimeout);
    }
    this.showZoomControls = true;
    this.zoomControlsTimeout = setTimeout(() => {
      this.showZoomControls = false;
    }, 3000);
  }

  /**
   * Checks if a point is inside a polygon using the ray casting algorithm
   * @param point The point to check (in pixel coordinates)
   * @param polygonPoints The vertices of the polygon (in pixel coordinates)
   * @returns Boolean indicating if the point is inside the polygon
   */
  isPointInPolygon(
    point: { x: number; y: number },
    polygonPoints: { x: number; y: number }[]
  ): boolean {
    if (!polygonPoints || polygonPoints.length < 3) {
      return false;
    }
    let inside = false;
    const n = polygonPoints.length;
    for (let i = 0, j = n - 1; i < n; j = i++) {
      const xi = polygonPoints[i].x;
      const yi = polygonPoints[i].y;
      const xj = polygonPoints[j].x;
      const yj = polygonPoints[j].y;
      const intersect =
        yi > point.y !== yj > point.y &&
        point.x < ((xj - xi) * (point.y - yi)) / (yj - yi) + xi;
      if (intersect) inside = !inside;
    }

    return inside;
  }

  /**
   * Finds which polygon (if any) contains the given point
   * @param point The point to check (in pixel coordinates)
   * @returns The polygon ID if the point is inside a polygon, null otherwise
   */
  findPolygonContainingPoint(point: { x: number; y: number }): string | null {
    if (!this.polygons || this.polygons.length === 0) {
      return null;
    }
    for (const polygon of this.polygons) {
      if (!polygon.pointsPercent || polygon.pointsPercent.length < 3) {
        continue;
      }
      const originalDisplayWidth = this.displayWidth / this.scale;
      const originalDisplayHeight = this.displayHeight / this.scale;
      const pixelPolygon = polygon.pointsPercent.map((p) => ({
        x: p.x * originalDisplayWidth,
        y: p.y * originalDisplayHeight,
      }));
      if (this.isPointInPolygon(point, pixelPolygon)) {
        return polygon.id;
      }
    }
    return null;
  }

  /**
   * Expands floor plan
   */
expandFloorPlan() {
  this.isFullScreen = !this.isFullScreen;
  this.expand.emit(this.isFullScreen);

  if (this.isFullScreen) {
    // Target the zone-plan element specifically, not the whole document
    const el = this.planContainer.nativeElement.closest('.zone-plan') 
               ?? this.planContainer.nativeElement;
    if (el.requestFullscreen) el.requestFullscreen();
    else if ((el as any).webkitRequestFullscreen) (el as any).webkitRequestFullscreen();
    else if ((el as any).mozRequestFullScreen) (el as any).mozRequestFullScreen();
  } else {
    if (document.exitFullscreen) document.exitFullscreen();
    else if ((document as any).webkitExitFullscreen) (document as any).webkitExitFullscreen();
    else if ((document as any).mozCancelFullScreen) (document as any).mozCancelFullScreen();
  }

  setTimeout(() => {
    this.setupCanvas();
    this._setPlotConfig();
    this.updateInfoIconPositions();
  }, 200);
}

  /**
   * Expands image to fullscreen
   * @param imageUrl - The URL of the image to expand
   */
  expandImageFullScreen(imageUrl: string): void {
    if (!imageUrl) {
      return;
    }

    // Create a fullscreen image viewer
    const modal = document.createElement('div');
    modal.className = 'image-fullscreen-modal';
    modal.innerHTML = `
      <div class="modal-content">
        <button class="close-btn" aria-label="Close" type="button">
          <span aria-hidden="true">✕</span>
        </button>
        <img src="${imageUrl}" alt="Expanded Image" class="fullscreen-image">
      </div>
    `;

    document.body.appendChild(modal);

    // Handle close button click
    const closeBtn = modal.querySelector('.close-btn') as HTMLButtonElement;
    closeBtn?.addEventListener('click', () => {
      modal.remove();
    });

    // Handle backdrop click
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        modal.remove();
      }
    });

    // Handle escape key
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        modal.remove();
        document.removeEventListener('keydown', handleEscape);
      }
    };
    document.addEventListener('keydown', handleEscape);
  }

  private _notifyUser(): void {
    this._apiService.toasterService({
      type: 'warning',
      msg: 'Worktable overlaps with an existing Worktable. Please redraw.',
    });
  }

  private _labelBasedOnCategory(category: string, fieldType: string): string {
    const categoryLabelConfig = {
      [this.categoryList['workBench']]: {
        title: 'ZONE_PLAN_DIALOG_DELETE_TITLE_WORKBENCH',
        content: 'ZONE_PLAN_DIALOG_DELETE_CONTENT_WORKBENCH',
      },
    };
    const defaultLabels = {
      title: 'ZONE_PLAN_DIALOG_DELETE_TITLE',
      content: 'ZONE_PLAN_DIALOG_DELETE_CONTENT',
    };
    const categoryConfig = categoryLabelConfig[category];
    const messageKey = categoryConfig?.[fieldType] || defaultLabels[fieldType];
    return Messages?.[messageKey] || '';
  }

  /**
   * Check if a line segment intersects with any existing polygon
   * @param startPoint - Starting point of the line segment
   * @param endPoint - Ending point of the line segment
   * @returns true if intersection found, false otherwise
   */
  private checkLineIntersectsPolygons(
    startPoint: { x: number; y: number },
    endPoint: { x: number; y: number }
  ): boolean {
    const startPercent = {
      x: startPoint.x / this.displayWidth,
      y: startPoint.y / this.displayHeight,
    };
    const endPercent = {
      x: endPoint.x / this.displayWidth,
      y: endPoint.y / this.displayHeight,
    };
    for (const polygon of this.polygons) {
      if (polygon.isPending && polygon.id === this.pendingPolygonId) {
        continue; // Skip the polygon currently being drawn
      }
      if (
        this.lineIntersectsPolygon(
          startPercent,
          endPercent,
          polygon.pointsPercent
        )
      ) {
        return true;
      }
    }
    return false;
  }

  /**
   * Check if a line segment intersects with a polygon
   * @param lineStart - Start point of line in percentage coordinates
   * @param lineEnd - End point of line in percentage coordinates
   * @param polygonPoints - Array of polygon points in percentage coordinates
   * @returns true if line intersects polygon, false otherwise
   */
  private lineIntersectsPolygon(
    lineStart: { x: number; y: number },
    lineEnd: { x: number; y: number },
    polygonPoints: { x: number; y: number }[]
  ): boolean {
    for (let i = 0; i < polygonPoints.length; i++) {
      const edgeStart = polygonPoints[i];
      const edgeEnd = polygonPoints[(i + 1) % polygonPoints.length];
      if (this.lineSegmentsIntersect(lineStart, lineEnd, edgeStart, edgeEnd)) {
        return true;
      }
    }
    return this.lineIntersectsPolygonInterior(
      lineStart,
      lineEnd,
      polygonPoints
    );
  }

  /**
   * Check if two line segments intersect
   * @param line1Start - Start point of first line
   * @param line1End - End point of first line
   * @param line2Start - Start point of second line
   * @param line2End - End point of second line
   * @returns true if lines intersect, false otherwise
   */
  private lineSegmentsIntersect(
    line1Start: { x: number; y: number },
    line1End: { x: number; y: number },
    line2Start: { x: number; y: number },
    line2End: { x: number; y: number }
  ): boolean {
    const x1 = line1Start.x,
      y1 = line1Start.y;
    const x2 = line1End.x,
      y2 = line1End.y;
    const x3 = line2Start.x,
      y3 = line2Start.y;
    const x4 = line2End.x,
      y4 = line2End.y;
    const denominator = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);
    if (Math.abs(denominator) < 1e-10) {
      return false;
    }
    const t = ((x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4)) / denominator;
    const u = -((x1 - x2) * (y1 - y3) - (y1 - y2) * (x1 - x3)) / denominator;
    return t >= 0 && t <= 1 && u >= 0 && u <= 1;
  }

  /**
   * Check if line passes through polygon interior using ray casting
   * @param lineStart - Start point of line
   * @param lineEnd - End point of line
   * @param polygonPoints - Array of polygon points
   * @returns true if line passes through polygon interior
   */
  private lineIntersectsPolygonInterior(
    lineStart: { x: number; y: number },
    lineEnd: { x: number; y: number },
    polygonPoints: { x: number; y: number }[]
  ): boolean {
    const startInside = this.isPointInPolygon(lineStart, polygonPoints);
    const endInside = this.isPointInPolygon(lineEnd, polygonPoints);
    return startInside !== endInside;
  }

  /**
   * Check if two polygons have any containment relationship (one inside another)
   * @param polygon1Points - Points of first polygon in percentage coordinates
   * @param polygon2Points - Points of second polygon in percentage coordinates
   * @returns true if one polygon contains the other
   */
  private checkPolygonContainment(
    polygon1Points: { x: number; y: number }[],
    polygon2Points: { x: number; y: number }[]
  ): boolean {
    // Check if polygon1 contains polygon2
    let polygon1ContainsPolygon2 = true;
    for (const point of polygon2Points) {
      if (!this.isPointInPolygon(point, polygon1Points)) {
        polygon1ContainsPolygon2 = false;
        break;
      }
    }

    if (polygon1ContainsPolygon2) {
      return true;
    }

    // Check if polygon2 contains polygon1
    let polygon2ContainsPolygon1 = true;
    for (const point of polygon1Points) {
      if (!this.isPointInPolygon(point, polygon2Points)) {
        polygon2ContainsPolygon1 = false;
        break;
      }
    }

    return polygon2ContainsPolygon1;
  }

  /**
   * Check if new polygon overlaps with existing polygons (intersections or containment)
   * @param newPolygonPoints - Points of new polygon in canvas coordinates
   * @returns true if overlap detected, false otherwise
   */
  private checkPolygonOverlap(
    newPolygonPoints: { x: number; y: number }[]
  ): boolean {
    // Convert to percentage coordinates
    const newPolygonPercent = newPolygonPoints.map((point) => ({
      x: point.x / this.displayWidth,
      y: point.y / this.displayHeight,
    }));

    // Check against all existing polygons (excluding the current one being drawn)
    for (const existingPolygon of this.polygons) {
      if (
        existingPolygon.isPending &&
        existingPolygon.id === this.pendingPolygonId
      ) {
        continue; // Skip the polygon currently being drawn
      }

      // Check for line intersections
      for (let i = 0; i < newPolygonPercent.length; i++) {
        const edgeStart = newPolygonPercent[i];
        const edgeEnd = newPolygonPercent[(i + 1) % newPolygonPercent.length];

        if (
          this.lineIntersectsPolygon(
            edgeStart,
            edgeEnd,
            existingPolygon.pointsPercent
          )
        ) {
          return true; // Intersection found
        }
      }

      // Check for containment (one polygon inside another)
      if (
        this.checkPolygonContainment(
          newPolygonPercent,
          existingPolygon.pointsPercent
        )
      ) {
        return true; // Containment found
      }
    }

    return false; // No overlaps
  }

  /**
   * Validate entire polygon for all types of overlaps before finalizing
   * @param polygonPoints - Points of polygon in canvas coordinates
   * @returns true if valid (no overlaps), false if overlaps detected
   */
  private validatePolygonComplete(
    polygonPoints: { x: number; y: number }[]
  ): boolean {
    return !this.checkPolygonOverlap(polygonPoints);
  }

  onPlotTooltipClick(event: MouseEvent, entityId: string): void {
    event.stopPropagation();
    this.activeTooltipEntityId = 
      this.activeTooltipEntityId === entityId ? null : entityId;
  }

  onTooltipButtonClick(event: MouseEvent, plot: ZonePlanEntityModel): void {
      event.stopPropagation();
      this._dialog.open(FloorplanOtaUpdateComponent, {
      data: { title: `Update ${plot?.category === 'Gateway' ? 'Configuration' : 'Mapping'}`, plot: plot },
      disableClose: true
    });
  }
}
